import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AudioLines, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CircleHelp, Copy, MoveHorizontal, Music, Plus, ScanLine, SkipBack, Trash2, Volume2, ZoomIn, ZoomOut } from "lucide-react";
import { RenderStatus } from "@web-shared/components/RenderStatus";
import { faderDbLabel, panLabel } from "@shared/mixer.js";
import { stripTokens } from "@shared/sync/track-id.js";
import type { TrackField, TrackFieldValue } from "@shared/sync/state.js";
import { clipColor, type Arrangement, type Lane, type LaneClip } from "@web-shared/lib/arrangement";
import { barsBeats, beats, short } from "@web-shared/lib/format";
import { beatUnit, beatsPerBar, FOUR_FOUR, type Meter } from "@web-shared/lib/meter";
import { DEFAULT_PATCH, PRESETS } from "@web-shared/lib/synth";
import { snapToEdges } from "@web-shared/lib/placement";
import { adaptiveGrid, gridLabel } from "@web-shared/lib/time-selection";
import { cn } from "@web-shared/lib/utils";
import { isBoolean, usePref } from "@web-shared/hooks/usePref";
import { useIsCompact } from "@web-shared/hooks/useIsNarrow";
import { Badge } from "@web-shared/components/ui/badge";
import { ContextMenu, type ContextMenuEntry } from "@web-shared/components/ui/context-menu";

export interface LaneAudioState {
  muted: Set<string>;
  soloed: Set<string>;
  onMute: (laneId: string, muted: boolean) => void;
  onSolo: (laneId: string, soloed: boolean) => void;
  /** True when `onMute`/`onSolo` write the Set's own flags — the page mixes
   * by editing the Set. The editing header's playback-only m/s would then
   * repeat its M/S, so they are left out. */
  mixesTheSet?: boolean;
  /** Lanes the Session View has taken over (their timeline clips are
   * silent), and Live's per-track "Back to Arrangement" for one of them. */
  overridden?: ReadonlySet<string>;
  onBackToArrangement?: (laneId: string) => void;
  /** Each MIDI lane's instrument (a preset id, lib/synth.ts — the seed's
   * own, shared/instruments.ts); absent ⇒ sine. No chooser without `onPatch`. */
  patches?: Record<string, string>;
  onPatch?: (laneId: string, patchId: string) => void;
}

/** A MIDI lane's instrument — what the browser plays it with. */
export function PatchSelect({ laneId, audio, className }: { laneId: string; audio: LaneAudioState; className?: string }) {
  if (!audio.onPatch) return null;
  return (
    <select
      value={audio.patches?.[laneId] ?? DEFAULT_PATCH.id}
      onChange={(e) => audio.onPatch?.(laneId, e.target.value)}
      onKeyDown={(e) => e.stopPropagation()}
      title="Instrument — what this track plays in the browser; Live plays its own"
      aria-label="Instrument"
      className={cn("h-4 rounded border border-border bg-transparent px-0.5 text-[9px] text-muted-foreground", className)}
    >
      {PRESETS.map((p) => (
        <option key={p.id} value={p.id}>
          {p.label}
        </option>
      ))}
    </select>
  );
}

/** Everything the view needs to become an editor. Absent ⇒ read-only, exactly as before. */
export interface EditState {
  selected: Set<string>;
  onSelect: (clipId: string | null, additive: boolean) => void;
  /** A rubber-band selection finished over these clips. */
  onSelectMany: (clipIds: string[], additive: boolean) => void;
  /** The same rubber band as a span of TIME across lanes — null on a plain
   * click. What the time-operation bar acts on. */
  onTimeSelect?: (sel: { start: number; end: number; laneIds: string[] } | null) => void;
  /** The current time selection, drawn as a band. */
  timeSelection?: { start: number; end: number; laneIds: string[] } | null;
  /** A finished drag. `targetLaneId` is null when the clip stayed on its lane. */
  onMove: (clipIds: string[], deltaBeats: number, targetLaneId: string | null) => void;
  /** The same drag with ⌥ held: the originals stay, copies land at the drop. */
  onCopyMove?: (clipIds: string[], deltaBeats: number, targetLaneId: string | null) => void;
  /** One clip dropped below the last lane: it gets a track of its own, as in
   * Live. Present ⇒ a drag can reach the row under the lanes. */
  onMoveToNewLane?: (clipId: string, deltaBeats: number, copy: boolean) => void;
  onResize: (clipId: string, edge: "start" | "end", deltaBeats: number) => void;
  onTrackField: (laneId: string, field: TrackField, value: TrackFieldValue) => void;
  onDeleteTrack: (laneId: string) => void;
  /** Double-click on a MIDI lane's empty space: draw an empty clip there. */
  onCreateMidiClip: (laneId: string, atBeat: number) => void;
  /** The MIDI lane header's ＋: a new clip where the page says (the insert
   * marker) — a touch screen's way to make one, with no double-click. */
  onAddMidiClip?: (laneId: string) => void;
  /** A library entry dropped on a lane (the panel's drag payload). */
  onDropLibrary?: (laneId: string, atBeat: number, payload: string) => void;
  /** Audio files from the desktop dropped on a lane. */
  onDropFiles?: (laneId: string, atBeat: number, files: File[]) => void;
  /** .mid files dropped on a lane. */
  onDropMidiFiles?: (laneId: string, atBeat: number, files: File[]) => void;
  /** A Session View clip dragged out of its slot onto the timeline. */
  onDropSlotClip?: (laneId: string, atBeat: number, clipId: string) => void;
  /** A piece of the room's own material dragged off the shelf onto a lane —
   * a COPY, unlike `onDropSlotClip`, which moves the clip it is given. */
  onDropMaterial?: (laneId: string, atBeat: number, clipId: string) => void;
  /** A clip from the producer's own library ("Mine") dropped on a lane. */
  onDropMine?: (laneId: string, atBeat: number, libraryClipId: string) => void;
  /** A MIDI idea from the producer's library, carried by a finger onto a lane. */
  onDropMineMidi?: (laneId: string, atBeat: number, libraryMidiId: string) => void;
  /** Copy a lane and everything on it, right after it. */
  onDuplicateTrack?: (laneId: string) => void;
  /** Move a lane up or down in the Set's display order. */
  onReorderTrack?: (laneId: string, direction: -1 | 1) => void;
  /** Proposed but not yet confirmed by the room. */
  pending: Set<string>;
  /** Beats. Drags snap to this so float jitter never costs a rebuild in Live. 0 = free.
   * Ignored when `adaptiveGrid` is present. */
  grid: number;
  /** Live's zoom-adaptive grid: the snap follows the zoom (lib/time-selection.ts
   * `adaptiveGrid`), `density` its ⌘1 / ⌘2 narrower and wider. */
  adaptiveGrid?: { density: number };
  /** Live's ⌘4. False ⇒ nothing snaps (drags still catch other clips' edges). */
  snap?: boolean;
  /** The grid in force, in beats (0 when snapping is off), as the zoom changes it. */
  onGridResolved?: (beats: number) => void;
  /** Click the grid readout: Live's ⌘4. */
  onSnapToggle?: () => void;
  /** A plain click in a lane (empty space, or a clip's body): the lane the
   * insert marker is now on — where a paste goes. */
  onCursorLane?: (laneId: string) => void;
}

/**
 * Where a right-click landed, as a DAW tells its menus apart: a clip, a lane's
 * empty space, the part of a lane a time selection covers, a track's header,
 * or the ruler. `beat` is on the grid the view draws.
 */
export type ArrangementMenuTarget =
  | { kind: "clip"; clip: LaneClip; lane: Lane; beat: number }
  | { kind: "lane"; lane: Lane; beat: number }
  | { kind: "span"; lane: Lane; beat: number; span: { start: number; end: number; laneIds: string[] } }
  /** `rename` focuses the header's name field — present while it shows one. */
  | { kind: "track"; lane: Lane; rename?: () => void }
  | { kind: "ruler"; beat: number };

/** Waveforms, when the page has decoded audio to draw them from. */
export interface WaveformSource {
  /** A clip's audio as `buckets` interleaved min/max pairs (lib/waveform.ts);
   * null until decoded. */
  peaksFor: (clip: LaneClip, laneId: string, buckets: number) => Float32Array | null;
  /** Bumps as objects decode — the trigger to redraw. */
  version: number;
  /** Present ⇒ asked once per clip shown, to fetch what `peaksFor` needs —
   * including a better object than the one it can already draw. */
  request?: (clip: LaneClip, laneId: string) => void;
}

import { LIBRARY_DRAG_TYPE, MATERIAL_DRAG_TYPE, MINE_DRAG_TYPE, SLOT_DRAG_TYPE } from "@web-shared/lib/drag-types";
import { DROP_LANE_ATTR, LIFT_DROP, LIFT_LEAVE, LIFT_OVER, type LiftDetail, type LiftEvent } from "@web-shared/lib/lift-drag";
import { drawEnvelope, prepareCanvas } from "@web-shared/lib/waveform";
export { LIBRARY_DRAG_TYPE };

const LANE_HEIGHTS = { compact: 22, normal: 30, tall: 56 } as const;
const LANE_HEIGHTS_EDIT = { compact: 40, normal: 50, tall: 84 } as const;
const LANE_GAP = 4;
const RULER_H = 20;
/** A phone's ruler: tall enough to land a thumb on, under the overview strip
 * whose buttons sit right above it. */
const RULER_H_MOBILE = 32;
const MIN_BEATS = 16;
/** Track-header column. 168px is half a phone, so it shrinks with the viewport. */
const HEADER_W_DESKTOP = 168;
const HEADER_W_MOBILE = 58;
/** Editing needs room for the Set's own flags and faders alongside the playback ones. */
const HEADER_W_EDIT_DESKTOP = 240;
const HEADER_W_EDIT_MOBILE = 132;
/** Collapsed on a phone: a colour chip and nothing else. 132px of names was a
 *  third of the screen, and the lane strip below already holds the controls. */
const HEADER_W_MOBILE_SLIM = 36;
/** Grab zone for an edge resize, in px at each end of a clip. */
const EDGE_PX = 6;
/** A fingertip's: a thumb can't find 6px, and on a phone a trim is as common
 *  as on a desktop. */
const TOUCH_EDGE_PX = 16;
/** Below this a drag is a click that wobbled, not a move. */
const DRAG_SLOP_PX = 3;
/** A clip held this close to an edge of the view scrolls it that way… */
const AUTOSCROLL_ZONE_PX = 56;
/** …this fast at the edge itself (px/s), easing off across the zone. */
const AUTOSCROLL_SPEED = 900;

/** The nearest ancestor that scrolls vertically — the page, failing any. */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  for (let p = el?.parentElement ?? null; p; p = p.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(p).overflowY)) return p;
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
}

/** −1…1: how hard a pointer at `pos` pushes on the `lo` or `hi` edge. */
function edgePush(pos: number, lo: number, hi: number): number {
  if (pos < lo + AUTOSCROLL_ZONE_PX) return -Math.min(1, (lo + AUTOSCROLL_ZONE_PX - pos) / AUTOSCROLL_ZONE_PX);
  if (pos > hi - AUTOSCROLL_ZONE_PX) return Math.min(1, (pos - (hi - AUTOSCROLL_ZONE_PX)) / AUTOSCROLL_ZONE_PX);
  return 0;
}
/**
 * A press this long with a finger on a clip: an audio clip opens in the clip
 * editor when the page offers one (`onAudioHold`); otherwise it adds the clip
 * to the selection — touch's shift-click, since the rubber band steps aside
 * for touch (`beginTimelineGesture`) so the timeline can still be panned.
 */
const LONG_PRESS_MS = 450;
/** A finger that travels further than this during a hold is scrolling. */
const HOLD_SLOP_PX = 10;
/** With the grid off, a clip edge this close to another's snaps to it. */
const EDGE_SNAP_PX = 8;
/** Empty bars kept past the last clip, so there is somewhere to put a new one.
 *  A flat 5% was ~2 beats on a short Set — not a bar to drop into. */
const HEADROOM_BARS = 4;
/** What one "extend" adds, and how far past the content it may ever reach. */
const TAIL_STEP_BARS = 4;
const TAIL_MAX_BARS = 64;
/** The minimap strip on a phone: tall enough to be a target, with the row of
 *  controls beside it. */
const STRIP_H = 40;
/** Zoom multipliers relative to "fit the whole arrangement". */
const ZOOM_MIN = 1;
const ZOOM_MAX = 64;
const ZOOM_STEP = 1.5;
/** ⌘+scroll / a trackpad pinch: zoom by exp(-deltaY × this). A pinch's
 * small deltas glide; a mouse notch (~100) is a bit over one ZOOM_STEP.
 * 0.004 (exactly one step a notch) felt sluggish to pinch in with. */
const WHEEL_ZOOM_RATE = 0.006;
/** Follow pages when the playhead gets this close to the right edge, and
 * lands it this far in from the left. */
const FOLLOW_EDGE_PX = 8;
const FOLLOW_LEAD_PX = 40;
/** A ruler label needs about this much room to not collide with the next. */
const RULER_LABEL_PX = 30;
/** A beat that is at least this wide gets sub-beat gridlines. */
const FINE_GRID_MIN_PX = 40;

type DragMode = "move" | "start" | "end";

interface Drag {
  mode: DragMode;
  /** ⌥ was down at the start: a copy, not a move. */
  copy: boolean;
  /** The clip under the pointer. Others in the selection follow it. */
  anchorId: string;
  clipIds: string[];
  anchorLaneIndex: number;
  deltaBeats: number;
  laneOffset: number;
  moved: boolean;
  /** A long press took the clip OUT of the selection: there is nothing under
   *  the finger to drag any more. */
  cancelled?: boolean;
}

/** A rubber band, in beats × lane indices, while it is being drawn. */
interface Marquee {
  fromBeat: number;
  toBeat: number;
  fromLane: number;
  toLane: number;
}

interface Props {
  arrangement: Arrangement;
  /** Live rooms show revs and link clip audio; stored snapshots have neither. */
  live?: boolean;
  /** Click a clip to hear just that clip. Works whether or not the transport is running. */
  onClipPlay?: (clip: LaneClip, lane: Lane) => void;
  /** Open a clip's underlying object in the docked R2 player. */
  onClipOpen?: (key: string) => void;
  /** Open a MIDI clip in the piano roll (double-click, or the hover footer). */
  onMidiOpen?: (clip: LaneClip) => void;
  /** Open an audio clip in the clip editor (double-click). */
  onAudioOpen?: (clip: LaneClip, lane: Lane) => void;
  /** A finger held on an audio clip (a phone's way in to the clip editor).
   * Absent ⇒ a hold adds the clip to the selection, as it does for MIDI. */
  onAudioHold?: (clip: LaneClip, lane: Lane) => void;
  emptyLabel?: string;
  /** What an arrangement with no lanes shows instead of `emptyLabel` — a
   * page that can make tracks offers to. */
  emptyState?: ReactNode;
  /** Playhead position in BEATS. Undefined hides the playhead entirely. */
  playheadBeats?: number;
  /** Click/drag anywhere on the timeline to move the playhead there. */
  onSeekBeats?: (beats: number) => void;
  /** Live's scrub area: present ⇒ a click in the RULER calls this instead
   * of `onSeekBeats` (a jump, quantized by the page while playing), and
   * clicks in the tracks keep `onSeekBeats` (the insert marker). A drag in
   * the ruler moves the marker with `onSeekBeats` and calls this on release. */
  onScrubBeats?: (beats: number) => void;
  /** Live's insert marker, in beats: where Space plays from. Absent ⇒ not drawn. */
  insertBeats?: number;
  /** Per-lane mute/solo, wired to the player's gain nodes. */
  audio?: LaneAudioState;
  /** Present ⇒ clips can be dragged, resized and selected. */
  edit?: EditState;
  waveform?: WaveformSource;
  /** Playback loop region, in beats, drawn on the ruler. */
  loopBeats?: { start: number; end: number } | null;
  /** Shift-drag on the ruler sets the loop region; a shift-click clears it. */
  onLoopBeats?: (range: { start: number; end: number } | null) => void;
  /** Ruler labels: raw beats, or Live-style bars (bar, or bar.beat between). */
  timeFormat?: "beats" | "bars";
  /** The meter bar lines and bar labels follow. */
  meter?: Meter;
  /** Ask the view to zoom so [start, end] fills the viewport (end ≤ start ⇒ fit
   * everything). A new `nonce` is a new request. */
  zoomRequest?: { start: number; end: number; nonce: number } | null;
  /** Row height for the lanes. */
  laneHeight?: "compact" | "normal" | "tall";
  /** The height the whole view may take. Given, the lanes grow (up to twice
   * `laneHeight`) to fill it rather than leave the bottom of the screen empty
   * under a five-track Set; never shrink below `laneHeight`. */
  fillHeight?: number;
  /** Page the view along with the playhead. Given ⇒ the page owns the
   * switch; absent ⇒ the view keeps it as a per-browser pref (default off)
   * with its own toggle in the ruler corner. Scrolling or zooming away while
   * it runs pauses it until the playhead is back in view, as Live. */
  follow?: boolean;
  /** Locators on the ruler (a per-browser aid). Click seeks; double-click removes. */
  markers?: ReadonlyArray<{ beat: number; name: string }>;
  onMarkerClick?: (beat: number) => void;
  onMarkerRemove?: (beat: number) => void;
  /** Mobile: tapping a lane header selects it for a bottom-panel mixer. */
  onLaneSelect?: (laneId: string) => void;
  /** Given ⇒ a "New track" button heads the track column (desktop), where
   * Live and FL keep theirs: at the top of the list it adds to. */
  onNewTrack?: () => void;
  /**
   * Given ⇒ a right-click opens a menu, its entries what this returns for the
   * place clicked (asked as the menu draws, so they follow the selection the
   * click just made). Before it opens the view does what Live's right-click
   * does: a clip outside the selection becomes the selection, and the insert
   * marker moves to the beat under the pointer. An empty list ⇒ no menu.
   * A mouse or a pen only: a finger's hold already opens a clip's editor.
   */
  contextMenu?: (target: ArrangementMenuTarget) => ContextMenuEntry[];
}

function useHeaderWidth(editing: boolean, collapsed: boolean): { headerWidth: number; narrow: boolean } {
  const narrow = useIsCompact();
  // Collapsing is a phone affordance: a desktop column is not what is scarce.
  if (narrow && collapsed) return { headerWidth: HEADER_W_MOBILE_SLIM, narrow };
  const headerWidth = editing
    ? (narrow ? HEADER_W_EDIT_MOBILE : HEADER_W_EDIT_DESKTOP)
    : (narrow ? HEADER_W_MOBILE : HEADER_W_DESKTOP);
  return { headerWidth, narrow };
}

/**
 * How many beats the timeline covers: the content, plus empty room past the
 * last clip to drop into, plus however much the producer has asked for on top
 * of that by extending. Pure, so the arithmetic can be tested on its own.
 */
export function timelineSpan(length: number, beatsPerBar: number, tailBeats: number): number {
  const headroom = Math.max(length * 0.05, HEADROOM_BARS * beatsPerBar);
  const tail = Math.max(0, Math.min(tailBeats, TAIL_MAX_BARS * beatsPerBar));
  return Math.max(length + headroom + tail, MIN_BEATS);
}

/** Width of the scrolling timeline viewport, kept current through resizes.
 * `mounted` re-arms the observer once the element exists — the view renders
 * an empty-state placeholder (no viewport) until the first lane appears. */
function useViewportWidth(ref: React.RefObject<HTMLDivElement | null>, mounted: boolean): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, mounted]);
  return width;
}

/**
 * The arrangement, drawn. One lane per track, one rect per clip positioned by
 * beat — the same picture Live shows, which is the only way to see at a glance
 * what the room actually believes rather than reading a table of numbers.
 *
 * Geometry is in PIXELS PER BEAT, not percent: at "fit" the whole arrangement
 * spans the viewport, and zooming in scrolls the timeline under a fixed
 * header column, so a sixteenth on a five-minute arrangement is still a
 * grabbable thing.
 */
export function ArrangementView({
  arrangement,
  live = false,
  onClipPlay,
  onClipOpen,
  onMidiOpen,
  onAudioOpen,
  onAudioHold,
  emptyLabel,
  emptyState,
  playheadBeats,
  onSeekBeats,
  onScrubBeats,
  insertBeats,
  audio,
  edit,
  waveform,
  loopBeats,
  onLoopBeats,
  timeFormat = "beats",
  meter = FOUR_FOUR,
  markers,
  onMarkerClick,
  onMarkerRemove,
  onLaneSelect,
  zoomRequest,
  laneHeight = "normal",
  fillHeight,
  follow: followProp,
  onNewTrack,
  contextMenu,
}: Props) {
  const [hovered, setHovered] = useState<LaneClip | null>(null);
  // The open right-click menu: where, on what, and a key so a second
  // right-click opens a fresh one rather than moving the first.
  // `touch`: a finger's hold opened it, so its items are sized for a thumb.
  const [menu, setMenu] = useState<{ x: number; y: number; target: ArrangementMenuTarget; key: number; touch: boolean } | null>(null);
  // A finger held still on empty lane space: the timer that opens its menu.
  const laneHold = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(laneHold.current), []);
  // contextmenu is a MouseEvent in some browsers: the pointer that pressed
  // last says whether it came from a finger (Android's long-press sends one).
  const lastPointerType = useRef<string>("mouse");
  const [scrollX, setScrollX] = useState(0);
  const bpb = beatsPerBar(meter);
  const label = (b: number): string => {
    if (timeFormat === "beats") return beats(b);
    const [bar, beat, tick] = barsBeats(b, meter).split(".");
    return tick === "1" ? (beat === "1" ? bar! : `${bar}.${beat}`) : barsBeats(b, meter);
  };
  const [drag, setDrag] = useState<Drag | null>(null);
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  const [zoom, setZoom] = useState(1);
  // Empty bars the producer has asked for past the end, to build into.
  const [tailBeats, setTailBeats] = useState(0);
  const [dropLane, setDropLane] = useState<number | null>(null);
  // Collapsing the header column is a per-browser choice, like lane height —
  // and on a phone it is the useful default, since the lane strip below
  // already carries the name, the faders and mute/solo for whichever lane you
  // tapped. Desktop ignores it (useHeaderWidth only honours it when narrow).
  const [headerCollapsed, setHeaderCollapsed] = usePref("arrangement.headerCollapsed", true, isBoolean);
  const [followPref, setFollowPref] = usePref("arrangement.follow", false, isBoolean);
  const follow = followProp ?? followPref;
  // A drag ends with a click event; without this the drop would also audition.
  const suppressClick = useRef(false);
  // A mouse press in a clip's body is a time gesture, not a clip click: the
  // click that follows it must not select the clip it happened to be over.
  const bodyPress = useRef(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const { headerWidth: headerW, narrow } = useHeaderWidth(edit !== undefined, headerCollapsed);
  const rulerH = narrow ? RULER_H_MOBILE : RULER_H;
  const { lanes, length } = arrangement;
  const viewportW = useViewportWidth(viewportRef, lanes.length > 0);
  const baseLaneH = (edit ? LANE_HEIGHTS_EDIT : LANE_HEIGHTS)[laneHeight];
  // Everything that is not a lane (overview, ruler, footer), measured off the
  // last layout: it does not depend on the lane height, so one pass settles.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [chromeH, setChromeH] = useState(0);
  const laneH =
    fillHeight !== undefined && lanes.length > 0
      ? Math.min(baseLaneH * 2, Math.max(baseLaneH, Math.floor((fillHeight - chromeH) / lanes.length) - LANE_GAP))
      : baseLaneH;
  const rowH = laneH + LANE_GAP;
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || fillHeight === undefined) return;
    const chrome = el.offsetHeight - (lanes.length + (overNewLane ? 1 : 0)) * rowH;
    if (Math.abs(chrome - chromeH) > 1) setChromeH(chrome);
  });

  // Fit leaves headroom past the last clip so there is somewhere to drop or
  // draw a new one, and `tailBeats` buys more of it on request.
  const span = timelineSpan(length, bpb, tailBeats);
  const pxPerBeat = viewportW > 0 ? (viewportW / span) * zoom : 0;
  const timelineW = Math.max(viewportW, span * pxPerBeat);

  /**
   * Add empty bars at the end. `zoom` is a multiple of "fit the whole span",
   * so a wider span at the same zoom would only squeeze the clips — scaling
   * zoom by the same ratio is what keeps px-per-beat put and actually makes
   * the timeline longer. Capped, so it cannot run away.
   */
  const extendTail = () => {
    const step = TAIL_STEP_BARS * bpb;
    const next = timelineSpan(length, bpb, tailBeats + step);
    if (next <= span) return;
    setTailBeats(tailBeats + step);
    setZoom((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z * (next / span))));
  };

  // Ruler + gridlines: the smallest musically sane spacing whose labels fit —
  // fractions of a beat, beats, then whole bars in the meter (so a 3/4 jam
  // rules 3, 6, 12 … and every label sits on a bar line).
  const step = useMemo(() => {
    if (pxPerBeat <= 0) return bpb;
    const candidates = [0.25, 0.5, 1, ...(bpb % 2 === 0 && bpb > 2 ? [bpb / 2] : []), ...[1, 2, 4, 8, 16, 32, 64].map((n) => n * bpb)];
    for (const candidate of candidates) {
      if (candidate * pxPerBeat >= RULER_LABEL_PX) return candidate;
    }
    return 128 * bpb;
  }, [pxPerBeat, bpb]);
  // Sub-grid: quarters of a whole-beat step; the meter's own beat (eighths
  // at 7/8) when the step is a bar that doesn't split in four.
  const fineByStep = pxPerBeat >= FINE_GRID_MIN_PX && step >= 1 ? (Number.isInteger(step) ? step / 4 : beatUnit(meter)) : null;

  // The grid everything snaps to. Adaptive (Live's default) follows the zoom,
  // so the lines you see are the places a click, a drag or a drop can land.
  const resolvedGrid = !edit
    ? 0
    : edit.adaptiveGrid
      ? adaptiveGrid(pxPerBeat, bpb, edit.adaptiveGrid.density)
      : edit.grid;
  const snapBeats = edit && edit.snap !== false ? resolvedGrid : 0;
  const onGridResolved = edit?.onGridResolved;
  useEffect(() => {
    if (pxPerBeat > 0) onGridResolved?.(snapBeats);
  }, [snapBeats, pxPerBeat, onGridResolved]);
  // Drawn at the snap grid when it is finer than the ruler's, as long as the
  // lines stay far enough apart to read.
  const fine = edit && resolvedGrid > 0 && resolvedGrid < step && resolvedGrid * pxPerBeat >= 6 ? resolvedGrid : fineByStep;

  // Every scroll the view makes itself goes through here, so `onScroll` can
  // tell it from the producer's own (which pauses follow), and so the
  // windowed grid moves in the same commit rather than a frame behind.
  const ownScroll = useRef<number | null>(null);
  const followPaused = useRef(false);
  const scrollViewTo = (el: HTMLDivElement, x: number) => {
    el.scrollLeft = Math.max(0, x);
    ownScroll.current = el.scrollLeft;
    setScrollX(el.scrollLeft);
  };

  // Zoom about an anchor: the beat under the pointer stays under it. Applied
  // in a layout effect once the new width is in the DOM — before paint, so
  // there is no frame of the wider timeline at the old scroll. `zoomTarget`
  // is the zoom asked for even before React has committed it, so a burst of
  // wheel events compounds instead of each starting from the same stale one.
  const zoomAnchor = useRef<{ beat: number; x: number } | null>(null);
  const zoomTarget = useRef(zoom);
  zoomTarget.current = zoom;
  useLayoutEffect(() => {
    const anchor = zoomAnchor.current;
    const el = viewportRef.current;
    if (!anchor || !el || pxPerBeat <= 0) return;
    zoomAnchor.current = null;
    scrollViewTo(el, anchor.beat * pxPerBeat - anchor.x);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pxPerBeat]);

  // Zoom to a span on request (Z on a selection; ⇧Z to fit). Each request is
  // honoured once — the view remounts when its tab is left and re-entered,
  // and an old request must not replay then. The scroll is applied AFTER
  // the wider timeline has committed (a layout effect keyed on the zoom),
  // not in a frame that may land before it.
  const handledZoomNonce = useRef<number | null>(null);
  const pendingScroll = useRef<{ zoom: number; startBeat: number } | null>(null);
  useEffect(() => {
    if (!zoomRequest || zoomRequest.nonce === handledZoomNonce.current) return;
    handledZoomNonce.current = zoomRequest.nonce;
    const el = viewportRef.current;
    if (!el || el.clientWidth <= 0) return;
    const length = zoomRequest.end - zoomRequest.start;
    if (!(length > 0)) {
      pendingScroll.current = { zoom: 1, startBeat: 0 };
      setZoom(1);
      return;
    }
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, (span / length) * 0.9));
    pendingScroll.current = { zoom: next, startBeat: zoomRequest.start };
    setZoom(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomRequest?.nonce]);
  useLayoutEffect(() => {
    const want = pendingScroll.current;
    const el = viewportRef.current;
    if (!want || !el || want.zoom !== zoom) return;
    pendingScroll.current = null;
    followPaused.current = true;
    scrollViewTo(el, want.startBeat * pxPerBeat - el.clientWidth * 0.05);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, span]);

  // Keep the playhead in view while it runs, page by page rather than a
  // constant scroll that would make the clips unreadable — Live's Follow.
  //
  // Only when the PLAYHEAD moves. Depending on `pxPerBeat` and `zoom` too meant
  // every zoom — a pinch, a button, extending the end — paged back to wherever
  // the playhead was parked, so scrolling out to the empty bars to build in
  // them snapped you to bar 1 the moment you got there.
  //
  // Scroll or zoom away while it runs and it lets go (`followPaused`) rather
  // than yanking you back on the next frame; it takes hold again once the
  // playhead is on screen — scroll back to it, click the ruler, or let a loop
  // bring it round. A layout effect, so the page turns in the same paint as
  // the playhead that crossed the edge, not one frame after.
  const geometry = useRef({ pxPerBeat, zoom });
  geometry.current = { pxPerBeat, zoom };
  useLayoutEffect(() => {
    followPaused.current = false;
  }, [follow]);
  useLayoutEffect(() => {
    const el = viewportRef.current;
    const { pxPerBeat: perBeat, zoom: z } = geometry.current;
    if (!follow || !el || playheadBeats === undefined || perBeat <= 0 || z <= 1) return;
    const x = playheadBeats * perBeat;
    if (x >= el.scrollLeft && x <= el.scrollLeft + el.clientWidth - FOLLOW_EDGE_PX) {
      followPaused.current = false;
      return;
    }
    if (!followPaused.current) scrollViewTo(el, x - FOLLOW_LEAD_PX);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playheadBeats, follow]);

  // ⌘+scroll and trackpad pinches (which arrive as ctrl+wheel). Native and
  // non-passive: React's onWheel is passive, so its preventDefault was
  // ignored and a pinch zoomed the whole page along with the timeline.
  const onZoomWheel = useRef<(e: WheelEvent) => void>(() => {});
  const hasLanes = lanes.length > 0;
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => onZoomWheel.current(e);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [hasLanes]);

  if (lanes.length === 0) {
    if (emptyState !== undefined) return <>{emptyState}</>;
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {emptyLabel ?? "No tracks in this arrangement."}
      </div>
    );
  }

  // A clip held below the last lane: the row a new track would take.
  const overNewLane = drag !== null && drag.mode === "move" && drag.anchorLaneIndex + drag.laneOffset >= lanes.length;
  const height = rulerH + (lanes.length + (overNewLane ? 1 : 0)) * rowH;
  const px = (b: number) => b * pxPerBeat;
  const snap = (b: number) => (snapBeats > 0 ? Math.round(b / snapBeats) * snapBeats : b);
  /** Client X → beats on the timeline, scroll included. */
  const beatAt = (clientX: number): number => {
    const el = viewportRef.current;
    if (!el || pxPerBeat <= 0) return 0;
    const box = el.getBoundingClientRect();
    return Math.max(0, Math.min((clientX - box.left + el.scrollLeft) / pxPerBeat, span));
  };
  /** Client Y → lane index, clamped. */
  const laneAt = (clientY: number): number => {
    const el = viewportRef.current;
    if (!el) return 0;
    const box = el.getBoundingClientRect();
    const y = clientY - box.top + el.scrollTop - rulerH;
    return Math.max(0, Math.min(lanes.length - 1, Math.floor(y / rowH)));
  };

  /** Zoom about a client X, so the beat under the pointer stays put. A
   * zoom is navigating, so it lets go of follow like a scroll does. */
  const zoomTo = (next: number, clientX?: number) => {
    const el = viewportRef.current;
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, next));
    if (!el || clamped === zoomTarget.current) return;
    const box = el.getBoundingClientRect();
    const anchorX = clientX === undefined ? el.clientWidth / 2 : clientX - box.left;
    // The DOM still holds the committed zoom's scroll and width, so the
    // anchor is measured against the committed geometry.
    const beat = (el.scrollLeft + anchorX) / Math.max(geometry.current.pxPerBeat, 1e-6);
    zoomAnchor.current = { beat, x: anchorX };
    zoomTarget.current = clamped;
    followPaused.current = true;
    setZoom(clamped);
  };
  onZoomWheel.current = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const el = viewportRef.current;
    // Lines and pages (Firefox, some mice) to pixels.
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? (el?.clientHeight ?? 400) : 1;
    zoomTo(zoomTarget.current * Math.exp(-e.deltaY * unit * WHEEL_ZOOM_RATE), e.clientX);
  };

  /**
   * Start a clip drag. Mirrors the timeline's scrub gesture — window-level
   * listeners with a pointercancel teardown, because a touch drag the browser
   * takes over never fires pointerup and the listeners would leak.
   */
  function beginDrag(e: React.PointerEvent<HTMLButtonElement>, clip: LaneClip, laneIndex: number) {
    if (!edit || pxPerBeat <= 0) return;
    // The second button opens the menu; it never drags.
    if (e.button === 2) return;
    const box = e.currentTarget.getBoundingClientRect();

    const offX = e.clientX - box.left;
    // A finger gets a quarter of a narrow clip at each end (a phone's clips
    // are narrow), and a middle half to move it by.
    const edgePx = e.pointerType === "mouse" ? EDGE_PX : Math.min(TOUCH_EDGE_PX, box.width / 4);
    const resizable = e.pointerType === "mouse" ? box.width > EDGE_PX * 3 : box.width >= 24;
    const mode: DragMode = !resizable
      ? "move"
      : offX < edgePx
        ? "start"
        : offX > box.width - edgePx
          ? "end"
          : "move";

    // Live's two halves of a clip: the title bar IS the clip (select, move,
    // ⌥-copy), the body is TIME — a click places the insert marker there, a
    // drag selects a span, exactly as on the empty lane. Left to bubble to the
    // viewport's gesture. A finger keeps the whole clip: a 20px bar is no
    // target for a thumb, and touch has no time selection to draw.
    bodyPress.current = false;
    if (mode === "move" && e.pointerType === "mouse" && (e.target as HTMLElement).closest("[data-clip-body]")) {
      bodyPress.current = true;
      const clear = () => {
        window.removeEventListener("pointerup", clear);
        window.setTimeout(() => {
          bodyPress.current = false;
        }, 0);
      };
      window.addEventListener("pointerup", clear);
      return;
    }

    // A multi-clip drag moves horizontally only: the selection can straddle
    // lanes, and "everyone down one" has no single sane clamp.
    const group =
      mode === "move" && edit.selected.has(clip.id) && edit.selected.size > 1
        ? [...edit.selected]
        : [clip.id];

    const state: Drag = {
      mode,
      copy: mode === "move" && e.altKey && edit.onCopyMove !== undefined,
      anchorId: clip.id,
      clipIds: group,
      anchorLaneIndex: laneIndex,
      deltaBeats: 0,
      laneOffset: 0,
      moved: false,
    };
    setDrag(state);

    // A finger held still on a clip: it opens in its editor (the clip editor,
    // the piano roll) when the room offers one; otherwise it toggles in the
    // selection.
    // Only a finger: a mouse has shift-click and double-click, and a careful
    // hand resting before a deliberate drag would be interrupted.
    let longPress: number | undefined;
    const button = e.currentTarget;
    const clearLongPress = () => {
      delete button.dataset.holding;
      if (longPress !== undefined) {
        window.clearTimeout(longPress);
        longPress = undefined;
      }
    };
    const lane = lanes[laneIndex];
    const opens = lane !== undefined && (clip.kind === "midi" ? onMidiOpen !== undefined : onAudioHold !== undefined);
    if (mode === "move" && (e.pointerType === "touch" || e.pointerType === "pen")) {
      // The clip fills up under the finger as the hold charges (CSS).
      if (opens) button.dataset.holding = "";
      longPress = window.setTimeout(() => {
        longPress = undefined;
        delete button.dataset.holding;
        if (opens) {
          navigator.vibrate?.(14);
          suppressClick.current = true;
          state.cancelled = true;
          setDrag(null);
          edit.onSelect(clip.id, false);
          if (clip.kind === "midi") onMidiOpen?.(clip);
          else onAudioHold?.(clip, lane);
          return;
        }
        const had = edit.selected.has(clip.id);
        edit.onSelect(clip.id, true);
        navigator.vibrate?.(10);
        // The lift that ends this gesture still fires the clip's onClick, which
        // would select just this clip — collapsing the selection the press just
        // grew — and audition it.
        suppressClick.current = true;
        if (had) {
          state.cancelled = true;
          setDrag(null);
        } else {
          // A drag on from here carries the selection as it now stands, not the
          // one captured when the finger landed.
          state.clipIds = [...new Set([...edit.selected, clip.id])];
          setDrag({ ...state });
        }
      }, LONG_PRESS_MS);
    }

    // With the grid off, drags snap to other clips' edges instead (within a
    // few pixels) — the places a clip is meant to butt up against.
    const edges =
      snapBeats > 0
        ? []
        : lanes.flatMap((l) => l.clips.filter((c) => !group.includes(c.id)).flatMap((c) => [c.start, c.start + c.duration]));
    const edgeSnap = (delta: number): number => {
      if (edges.length === 0) return delta;
      const threshold = EDGE_SNAP_PX / pxPerBeat;
      const start = clip.start + delta;
      const end = clip.start + clip.duration + delta;
      if (mode === "end") return snapToEdges(end, edges, threshold) - (clip.start + clip.duration);
      if (mode === "start") return snapToEdges(start, edges, threshold) - clip.start;
      const s = snapToEdges(start, edges, threshold);
      if (s !== start) return s - clip.start;
      return snapToEdges(end, edges, threshold) - (clip.start + clip.duration);
    };

    // The view scrolls under a held clip (below), so the distance dragged is
    // the pointer's travel plus however far the view has scrolled since the
    // press: the clip stays under the pointer either way.
    const hScroll = viewportRef.current;
    const vScroll = scrollParentOf(hScroll);
    const scrollOrigin = { left: hScroll?.scrollLeft ?? 0, top: vScroll?.scrollTop ?? 0 };
    const pointer = { x: e.clientX, y: e.clientY, free: false };

    const update = () => {
      if (state.cancelled) return; // a hold ended this gesture
      const dx = pointer.x - e.clientX + (hScroll?.scrollLeft ?? 0) - scrollOrigin.left;
      const dy = pointer.y - e.clientY + (vScroll?.scrollTop ?? 0) - scrollOrigin.top;
      state.moved ||= Math.abs(dx) > DRAG_SLOP_PX || Math.abs(dy) > DRAG_SLOP_PX;
      if (state.moved) clearLongPress(); // a press that travels is a drag
      // Live snaps the edge being dragged (the clip's start, for a move) onto
      // the grid, not the distance travelled: an off-grid clip lands on it.
      // ⌘/Ctrl drags free, as Live's does.
      const anchor = state.mode === "end" ? clip.start + clip.duration : clip.start;
      const raw = dx / pxPerBeat;
      state.deltaBeats = pointer.free ? raw : edgeSnap(snap(anchor + raw) - anchor);
      // `state.clipIds`, not `group`: a long press mid-gesture can have grown it,
      // and a multi-clip drag moves horizontally only.
      // One row past the last lane is a new track, where the room makes them.
      const lastRow = edit.onMoveToNewLane ? lanes.length : lanes.length - 1;
      state.laneOffset =
        state.mode === "move" && state.clipIds.length === 1
          ? Math.max(-laneIndex, Math.min(Math.round(dy / rowH), lastRow - laneIndex))
          : 0;
      setDrag({ ...state });
    };

    // A clip held at an edge of the view scrolls it that way, faster the
    // further out the pointer is — sideways for any drag, up and down for the
    // one kind that changes lanes. Whole pixels, carried over between frames,
    // so a slow scroll is not rounded away.
    let frame: number | undefined;
    let lastFrame = 0;
    const carry = { x: 0, y: 0 };
    const autoscroll = (t: number) => {
      frame = requestAnimationFrame(autoscroll);
      const dt = Math.min(0.05, (t - lastFrame) / 1000);
      lastFrame = t;
      if (!state.moved || state.cancelled) return;
      let scrolled = false;
      if (hScroll) {
        const box = hScroll.getBoundingClientRect();
        carry.x += edgePush(pointer.x, box.left, box.right) * AUTOSCROLL_SPEED * dt;
        const step = Math.trunc(carry.x);
        carry.x -= step;
        const before = hScroll.scrollLeft;
        if (step !== 0) hScroll.scrollLeft = before + step;
        scrolled ||= hScroll.scrollLeft !== before;
      }
      if (vScroll && state.mode === "move" && state.clipIds.length === 1) {
        const box = vScroll === document.scrollingElement ? null : vScroll.getBoundingClientRect();
        const lo = Math.max(0, box?.top ?? 0);
        const hi = Math.min(window.innerHeight, box?.bottom ?? window.innerHeight);
        carry.y += edgePush(pointer.y, lo, hi) * AUTOSCROLL_SPEED * dt;
        const step = Math.trunc(carry.y);
        carry.y -= step;
        const before = vScroll.scrollTop;
        if (step !== 0) vScroll.scrollTop = before + step;
        scrolled ||= vScroll.scrollTop !== before;
      }
      if (scrolled) update();
    };

    const move = (ev: PointerEvent) => {
      pointer.x = ev.clientX;
      pointer.y = ev.clientY;
      pointer.free = ev.metaKey || ev.ctrlKey;
      update();
      if (state.moved && frame === undefined) {
        lastFrame = performance.now();
        frame = requestAnimationFrame(autoscroll);
      }
    };

    const finish = (cancelled: boolean) => {
      clearLongPress();
      if (frame !== undefined) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      setDrag(null);
      if (cancelled) {
        // No click follows a cancelled pointer, so a suppression left standing
        // would be spent on the NEXT click instead — on some other clip.
        suppressClick.current = false;
        return;
      }
      if (state.cancelled) return; // a long press took the clip out of the selection
      if (!state.moved) return; // a click that wobbled — leave it to onClick
      // Swallow the click a mouse fires on release — dispatched before any
      // timer runs. A finger that travelled fires none, and a suppression
      // left standing would eat the NEXT tap instead.
      suppressClick.current = true;
      window.setTimeout(() => {
        suppressClick.current = false;
      }, 0);
      if (state.mode === "move" && laneIndex + state.laneOffset >= lanes.length) {
        edit.onMoveToNewLane?.(state.anchorId, state.deltaBeats, state.copy);
      } else if (state.mode === "move") {
        const target =
          state.laneOffset === 0 ? null : (lanes[laneIndex + state.laneOffset]?.id ?? null);
        if (state.deltaBeats !== 0 || target !== null) {
          if (state.copy) edit.onCopyMove?.(state.clipIds, state.deltaBeats, target);
          else edit.onMove(state.clipIds, state.deltaBeats, target);
        }
      } else if (state.deltaBeats !== 0) {
        edit.onResize(state.anchorId, state.mode, state.deltaBeats);
      }
    };
    const up = () => finish(false);
    const cancel = () => finish(true);

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  }

  /**
   * Pointer down on the timeline itself — an empty lane, or (a mouse) a
   * clip's body. Observing: scrub the playhead. Editing, as Live's
   * Arrangement does it: a click puts the insert marker there (on the grid)
   * on that lane; a drag selects a span of time across the lanes it crosses;
   * ⇧ grows the selection to take the click in. Clips lying wholly inside the
   * span are selected too, so a title-bar drag moves them together.
   */
  function beginTimelineGesture(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button === 2) return;
    const target = e.target as HTMLElement;
    const inClipBody = edit !== undefined && e.pointerType === "mouse" && target.closest("[data-clip-body]") !== null;
    if (target.closest("button, input") && !inClipBody) return;
    // Live: a click in a clip's body SELECTS the clip and puts the insert
    // marker there — one click, and ⌘E cuts at it, Clip View shows it. A
    // drag from the body is still a time selection, as on the empty lane.
    const bodyClipId = inClipBody ? (target.closest("[data-clip-id]")?.getAttribute("data-clip-id") ?? null) : null;
    // A finger on empty timeline scrolls it. The viewport allows panning on
    // both axes for touch (`touch-action` below), so there is no pointermove
    // to drag with once a pan takes over — and a scrub or a rubber band that
    // fought the scroll would leave the timeline unreachable past its first
    // screen. A tap that never moved still does what a click does.
    if (e.pointerType === "touch") {
      const origin = { x: e.clientX, y: e.clientY, beat: beatAt(e.clientX), lane: laneAt(e.clientY) };
      // Held still on a lane (not under the last one): its menu, as a
      // right-click would open it. A pan or a second finger lets go of it.
      let held = false;
      window.clearTimeout(laneHold.current);
      const onLane = target.closest("[data-lane-row]") !== null;
      if (contextMenu && onLane) {
        laneHold.current = window.setTimeout(() => {
          laneHold.current = undefined;
          const lane = lanes[origin.lane];
          if (!lane) return;
          held = true;
          navigator.vibrate?.(12);
          openMenuAt(origin.x, origin.y, { kind: "lane", lane, beat: edit ? snap(origin.beat) : origin.beat }, true);
        }, LONG_PRESS_MS);
      }
      const move = (ev: PointerEvent) => {
        if (Math.abs(ev.clientX - origin.x) > HOLD_SLOP_PX || Math.abs(ev.clientY - origin.y) > HOLD_SLOP_PX) {
          window.clearTimeout(laneHold.current);
        }
      };
      const off = () => {
        window.clearTimeout(laneHold.current);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", off);
      };
      const up = (ev: PointerEvent) => {
        off();
        if (held) return; // the hold was the gesture
        if (Math.abs(ev.clientX - origin.x) > DRAG_SLOP_PX || Math.abs(ev.clientY - origin.y) > DRAG_SLOP_PX) return;
        if (edit) {
          edit.onSelect(null, false);
          edit.onTimeSelect?.(null);
          const lane = lanes[origin.lane];
          if (lane) edit.onCursorLane?.(lane.id);
        }
        onSeekBeats?.(edit ? snap(origin.beat) : origin.beat);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", off);
      return;
    }
    if (!edit) {
      if (!onSeekBeats) return;
      onSeekBeats(beatAt(e.clientX));
      const move = (ev: PointerEvent) => onSeekBeats(beatAt(ev.clientX));
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
      return;
    }

    // Free while ⌘ is held, as every other snap in the view.
    const at = (clientX: number, free: boolean) => (free ? beatAt(clientX) : snap(beatAt(clientX)));
    const additive = e.metaKey || e.ctrlKey;
    const extend = e.shiftKey;
    const origin = { beat: at(e.clientX, additive), lane: laneAt(e.clientY), x: e.clientX, y: e.clientY };
    const band: Marquee = { fromBeat: origin.beat, toBeat: origin.beat, fromLane: origin.lane, toLane: origin.lane };
    // ⇧: anchor at whichever end of the current selection is farther from
    // the click, so the span grows (or shrinks) to the click, as Live's does.
    const current = edit.timeSelection;
    const rows = current ? lanes.map((l, i) => (current.laneIds.includes(l.id) ? i : -1)).filter((i) => i >= 0) : [];
    if (extend && current && rows.length > 0) {
      band.fromBeat = Math.abs(origin.beat - current.start) >= Math.abs(origin.beat - current.end) ? current.start : current.end;
      const top = Math.min(...rows);
      const bottom = Math.max(...rows);
      band.fromLane = origin.lane >= top ? top : bottom;
      if (origin.lane > top && origin.lane < bottom) band.toLane = bottom; // inside: keep the lanes it had
      setMarquee({ ...band });
    }
    let moved = extend && rows.length > 0;

    const move = (ev: PointerEvent) => {
      moved ||= Math.abs(ev.clientX - origin.x) > DRAG_SLOP_PX || Math.abs(ev.clientY - origin.y) > DRAG_SLOP_PX;
      if (!moved) return;
      band.toBeat = at(ev.clientX, ev.metaKey || ev.ctrlKey);
      band.toLane = laneAt(ev.clientY);
      setMarquee({ ...band });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setMarquee(null);
      const b0 = Math.min(band.fromBeat, band.toBeat);
      const b1 = Math.max(band.fromBeat, band.toBeat);
      const l0 = Math.min(band.fromLane, band.toLane);
      const l1 = Math.max(band.fromLane, band.toLane);
      if (!moved || b1 - b0 <= 1e-9) {
        // A click: a point in time on one lane — and, in a clip's body, that
        // clip (Live's one-click select-and-place).
        if (bodyClipId) edit.onSelect(bodyClipId, additive);
        else if (!additive) edit.onSelect(null, false);
        edit.onTimeSelect?.(null);
        const lane = lanes[origin.lane];
        if (lane) edit.onCursorLane?.(lane.id);
        onSeekBeats?.(origin.beat);
        return;
      }
      const hit: string[] = [];
      lanes.forEach((lane, i) => {
        if (i < l0 || i > l1) return;
        for (const c of lane.clips) if (c.start >= b0 - 1e-9 && c.start + c.duration <= b1 + 1e-9) hit.push(c.id);
      });
      edit.onSelectMany(hit, additive);
      edit.onTimeSelect?.({ start: b0, end: b1, laneIds: lanes.slice(l0, l1 + 1).map((l) => l.id) });
      // The marker goes to the selection's start: Space plays what was picked.
      onSeekBeats?.(b0);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  /** The time selection, when it covers this lane at this beat. */
  const spanAt = (laneId: string, beat: number) => {
    const sel = edit?.timeSelection;
    return sel && sel.laneIds.includes(laneId) && beat >= sel.start - 1e-9 && beat <= sel.end + 1e-9 ? sel : null;
  };

  /**
   * A right-click. Live's: what is under the pointer becomes what the menu
   * acts on before it opens — a clip outside the selection is selected, the
   * insert marker moves to the beat (so Paste and Split happen HERE), and a
   * time selection that covers the point stays, since its menu is the span's.
   */
  function openMenu(e: React.MouseEvent, target: ArrangementMenuTarget) {
    if (!contextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    // A finger's menus open on a hold (`openMenuAt` from the press handlers);
    // the contextmenu Android also sends for one is not a second opening.
    const pointer = (e.nativeEvent as Partial<PointerEvent>).pointerType || lastPointerType.current;
    if (pointer === "touch") return;
    openMenuAt(e.clientX, e.clientY, target, false);
  }

  /** Open the menu at a client point, after doing what a right-click does there. */
  function openMenuAt(x: number, y: number, target: ArrangementMenuTarget, touch: boolean) {
    if (!contextMenu) return;
    if (edit) {
      if (target.kind === "clip") {
        const span = spanAt(target.lane.id, target.beat);
        if (span) {
          target = { kind: "span", lane: target.lane, beat: target.beat, span };
        } else {
          if (!edit.selected.has(target.clip.id)) edit.onSelect(target.clip.id, false);
          edit.onTimeSelect?.(null);
          onSeekBeats?.(target.beat);
        }
        edit.onCursorLane?.(target.lane.id);
      } else if (target.kind === "lane") {
        const span = spanAt(target.lane.id, target.beat);
        if (span) {
          target = { kind: "span", lane: target.lane, beat: target.beat, span };
        } else {
          edit.onSelect(null, false);
          edit.onTimeSelect?.(null);
          onSeekBeats?.(target.beat);
        }
        edit.onCursorLane?.(target.lane.id);
      }
    }
    const at = target;
    setMenu((m) => ({ x, y, target: at, key: (m?.key ?? 0) + 1, touch }));
  }

  /** Scrub from the ruler, in both modes. Shift-drag draws the loop region. */
  function beginScrub(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button === 2) {
      e.stopPropagation();
      return;
    }
    if (onLoopBeats && e.shiftKey) {
      e.stopPropagation();
      const from = snap(beatAt(e.clientX));
      let to = from;
      const move = (ev: PointerEvent) => {
        to = snap(beatAt(ev.clientX));
        onLoopBeats(to === from ? null : { start: Math.min(from, to), end: Math.max(from, to) });
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
        if (to === from) onLoopBeats(null);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
      return;
    }
    if (onScrubBeats) {
      // One jump per gesture, on release: a jump per pixel would queue a
      // quantized jump each. A drag carries the marker along (`onSeekBeats`,
      // which moves the parked playhead too) so you see where it will land.
      e.stopPropagation();
      const originX = e.clientX;
      let at = beatAt(e.clientX);
      let dragged = false;
      const move = (ev: PointerEvent) => {
        dragged ||= Math.abs(ev.clientX - originX) > DRAG_SLOP_PX;
        if (!dragged) return;
        at = beatAt(ev.clientX);
        onSeekBeats?.(at);
      };
      const off = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", off);
      };
      const up = () => {
        off();
        onScrubBeats(at);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", off);
      return;
    }
    if (!onSeekBeats) return;
    e.stopPropagation();
    onSeekBeats(beatAt(e.clientX));
    const move = (ev: PointerEvent) => onSeekBeats(beatAt(ev.clientX));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  /** Where a clip should be drawn right now, drag included. */
  function displayed(clip: LaneClip, laneIndex: number) {
    if (!drag || !drag.clipIds.includes(clip.id)) return { clip, laneIndex };
    if (drag.mode === "move") {
      return {
        clip: { ...clip, start: Math.max(0, clip.start + drag.deltaBeats) },
        laneIndex: laneIndex + drag.laneOffset,
      };
    }
    const minLen = snapBeats > 0 ? snapBeats : 1 / 16;
    if (drag.mode === "end") {
      return { clip: { ...clip, duration: Math.max(minLen, clip.duration + drag.deltaBeats) }, laneIndex };
    }
    const shift = Math.min(Math.max(drag.deltaBeats, -clip.start), clip.duration - minLen);
    return { clip: { ...clip, start: clip.start + shift, duration: clip.duration - shift }, laneIndex };
  }

  const droppable = Boolean(
    edit?.onDropLibrary || edit?.onDropFiles || edit?.onDropMidiFiles || edit?.onDropSlotClip || edit?.onDropMaterial || edit?.onDropMine,
  );
  // A thumb's drag off the material shelf (lib/lift-drag.ts) arrives as DOM
  // events on the lane rows and is routed exactly as the HTML5 drop below:
  // the lane under the finger, the beat snapped to the grid. The handler is
  // read through a ref so the listeners register once.
  const liftRef = useRef<(type: string, d: LiftDetail) => void>(() => {});
  liftRef.current = (type, d) => {
    if (!edit) return;
    if (type === LIFT_LEAVE) {
      setDropLane(null);
      return;
    }
    const row = laneAt(d.clientY);
    if (type === LIFT_OVER) {
      if (row !== dropLane) setDropLane(row);
      return;
    }
    setDropLane(null);
    const lane = lanes[row];
    if (!lane) return;
    const at = snap(beatAt(d.clientX));
    if ("material" in d.payload) edit.onDropMaterial?.(lane.id, at, d.payload.material);
    else if ("mine" in d.payload) edit.onDropMine?.(lane.id, at, d.payload.mine);
    else if ("mineMidi" in d.payload) edit.onDropMineMidi?.(lane.id, at, d.payload.mineMidi);
  };
  // Re-run when the lanes or droppability change: the viewport may not be
  // in the DOM on the first pass (an empty room shows its prompt instead),
  // and an effect with no deps would never find it.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !droppable) return;
    const on = (e: Event) => liftRef.current(e.type, (e as LiftEvent).detail);
    for (const t of [LIFT_OVER, LIFT_LEAVE, LIFT_DROP]) el.addEventListener(t, on);
    return () => {
      for (const t of [LIFT_OVER, LIFT_LEAVE, LIFT_DROP]) el.removeEventListener(t, on);
    };
  }, [droppable, lanes.length]);
  // Gridlines and labels only for what is on screen, plus a viewport either
  // side for a fast scroll: zoomed in on a long Set the whole span is
  // thousands of them, and the view re-renders every frame the transport runs.
  const shownFrom = pxPerBeat > 0 ? Math.max(0, (scrollX - viewportW) / pxPerBeat) : 0;
  const shownTo = pxPerBeat > 0 ? Math.min(span, (scrollX + 2 * viewportW) / pxPerBeat) : span;
  const every = (spacing: number): number[] => {
    const out: number[] = [];
    for (let i = Math.ceil(shownFrom / spacing); i * spacing <= shownTo; i++) out.push(i * spacing);
    return out;
  };
  const gridBeats = every(step);
  const fineBeats = fine ? every(fine).filter((b) => b % step !== 0) : [];
  // Bar lines are the meter's, not the step's: at 7/8 a beat-level step
  // never lands on 3.5, so they are drawn from `bpb` in their own right.
  const barBeats = step < bpb ? every(bpb) : [];

  /**
   * Pinch to zoom. The only touch zoom there is — ⌘+scroll is a pointer's
   * privilege and the two buttons are a fallback, not a gesture. While two
   * fingers are down the viewport stops panning (it would fight the pinch)
   * and goes back to panning both axes on release.
   */
  const pinch = useRef<Map<number, number>>(new Map());
  const pinchStart = useRef<{ gap: number; zoom: number } | null>(null);
  const [pinching, setPinching] = useState(false);

  const pinchGap = () => {
    const xs = [...pinch.current.values()];
    return xs.length === 2 ? Math.abs(xs[0]! - xs[1]!) : 0;
  };
  const pinchMid = () => {
    const xs = [...pinch.current.values()];
    return xs.length === 2 ? (xs[0]! + xs[1]!) / 2 : 0;
  };
  const endPinch = (id: number) => {
    pinch.current.delete(id);
    if (pinch.current.size < 2) {
      pinchStart.current = null;
      setPinching(false);
    }
  };

  /** Scroll the viewport so `beat` sits in its middle. */
  const scrollToBeat = (beat: number) => {
    const el = viewportRef.current;
    if (!el || pxPerBeat <= 0) return;
    el.scrollLeft = Math.max(0, beat * pxPerBeat - el.clientWidth / 2);
  };

  const viewWindow = {
    from: scrollX / Math.max(timelineW, 1),
    width: Math.min(1, viewportW / Math.max(timelineW, 1)),
  };
  const minimap = (
    <Minimap
      lanes={lanes}
      span={span}
      height={narrow ? STRIP_H : undefined}
      window={viewWindow}
      playheadBeats={playheadBeats}
      onSeekTo={scrollToBeat}
      onNudge={(fraction) => {
        const el = viewportRef.current;
        if (el) el.scrollLeft = Math.max(0, fraction * timelineW);
      }}
    />
  );

  return (
    <div
      ref={rootRef}
      className="rounded-lg border border-border bg-card"
      onPointerDownCapture={(e) => {
        lastPointerType.current = e.pointerType;
      }}
    >
      {/* A phone keeps the overview up at all times: at zoom 1 it is the whole
          arrangement at a glance and a place to scrub, and once you zoom in it
          is the only way back across — a finger on the timeline itself is
          already spoken for by scrubbing and by dragging clips. A pointer gets
          it only when there is something off-screen to find. */}
      {narrow ? (
        <div className="flex items-stretch border-b border-border" style={{ height: STRIP_H }}>
          {/* Lines the overview up with the timeline it describes, and gives
              the collapse toggle a home that is not the 20px ruler corner. */}
          <div className="shrink-0 border-r border-border" style={{ width: headerW }}>
            <button
              type="button"
              onClick={() => setHeaderCollapsed(!headerCollapsed)}
              title={headerCollapsed ? "Show track names" : "Hide track names"}
              aria-label={headerCollapsed ? "Show track names" : "Hide track names"}
              aria-expanded={!headerCollapsed}
              className="flex h-full w-full items-center justify-center text-muted-foreground"
            >
              {headerCollapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
            </button>
          </div>
          <div className="relative min-w-0 flex-1">{minimap}</div>
          {followProp === undefined && (
            <StripButton
              onClick={() => setFollowPref(!follow)}
              title={follow ? "Following the playhead" : "Follow the playhead"}
              label="Follow the playhead"
              pressed={follow}
            >
              <ScanLine className={cn("size-4", follow && "text-primary")} />
            </StripButton>
          )}
          <StripButton
            onClick={() => zoomTo(zoom / ZOOM_STEP)}
            disabled={zoom <= ZOOM_MIN}
            title="Zoom out"
            label="Zoom out"
          >
            <ZoomOut className="size-4" />
          </StripButton>
          <StripButton
            onClick={() => zoomTo(zoom * ZOOM_STEP)}
            disabled={zoom >= ZOOM_MAX}
            title="Zoom in"
            label="Zoom in"
          >
            <ZoomIn className="size-4" />
          </StripButton>
          <StripButton
            onClick={extendTail}
            disabled={tailBeats >= TAIL_MAX_BARS * bpb}
            title={`Add ${TAIL_STEP_BARS} empty bars at the end`}
            label={`Add ${TAIL_STEP_BARS} empty bars at the end`}
          >
            <Plus className="size-4" />
          </StripButton>
        </div>
      ) : (
        zoom > 1 && minimap
      )}
      <div className="flex">
        {/* Track headers — a real column, so long names don't overlay the clips. */}
        <div className="shrink-0 border-r border-border" style={{ width: headerW }}>
          <div className="flex items-center justify-end gap-0.5 border-b border-border px-1" style={{ height: rulerH }}>
            {/* On a phone the zoom lives in the strip above, at a size a thumb
                can hit, and this corner is the way back to bar 1: a tap on the
                ruler's first bar sits right against the edge of the screen. */}
            {narrow && (onScrubBeats ?? onSeekBeats) && (
              <button
                type="button"
                onClick={() => {
                  (onScrubBeats ?? onSeekBeats)?.(0);
                  if (viewportRef.current) viewportRef.current.scrollLeft = 0;
                }}
                title="Back to bar 1"
                aria-label="Back to bar 1"
                className="-mx-1 flex h-full w-[calc(100%+0.5rem)] items-center justify-center text-muted-foreground active:text-foreground"
              >
                <SkipBack className="size-4" />
              </button>
            )}
            {!narrow && (
              <>
                {/* The top of the list it adds to, as in Live and FL — the
                    first thing in the column, and the only text in it. */}
                {onNewTrack && (
                  <button
                    type="button"
                    onClick={onNewTrack}
                    title="New track — audio or an instrument"
                    className="-ml-0.5 mr-auto flex h-full items-center gap-1 rounded px-1 text-[10px] text-foreground/70 hover:bg-white/5 hover:text-foreground"
                  >
                    <Plus className="size-3.5" />
                    New track
                  </button>
                )}
                {followProp === undefined && (
                  <button
                    type="button"
                    className={cn(
                      "flex size-5 items-center justify-center rounded hover:bg-white/5 hover:text-foreground",
                      follow ? "text-primary" : "text-foreground/70",
                    )}
                    onClick={() => setFollowPref(!follow)}
                    title={follow ? "Following the playhead — click to stop the view paging with it" : "Follow the playhead"}
                    aria-label="Follow the playhead"
                    aria-pressed={follow}
                  >
                    <ScanLine className="size-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  className="flex size-5 items-center justify-center rounded text-foreground/70 hover:bg-white/5 hover:text-foreground disabled:opacity-30"
                  disabled={zoom <= ZOOM_MIN}
                  onClick={() => zoomTo(zoom / ZOOM_STEP)}
                  title="Zoom out (⌘ + scroll)"
                  aria-label="Zoom out"
                >
                  <ZoomOut className="size-3.5" />
                </button>
                <button
                  type="button"
                  className="flex size-5 items-center justify-center rounded text-foreground/70 hover:bg-white/5 hover:text-foreground disabled:opacity-30"
                  disabled={zoom >= ZOOM_MAX}
                  onClick={() => zoomTo(zoom * ZOOM_STEP)}
                  title="Zoom in (⌘ + scroll)"
                  aria-label="Zoom in"
                >
                  <ZoomIn className="size-3.5" />
                </button>
              </>
            )}
          </div>
          {lanes.map((lane, i) => (
            <LaneHeader
              key={lane.id}
              lane={lane}
              index={i}
              height={laneH}
              live={live}
              edit={edit}
              audio={audio}
              slim={narrow && headerCollapsed}
              dropTarget={dropLane !== null && lanes[dropLane]?.id === lane.id}
              onMobileTap={narrow ? onLaneSelect : undefined}
              onMenu={contextMenu ? (e, rename) => openMenu(e, { kind: "track", lane, rename }) : undefined}
              onHoldMenu={contextMenu ? (x, y, rename) => openMenuAt(x, y, { kind: "track", lane, rename }, true) : undefined}
            />
          ))}
        </div>

        {/* Timeline — scrolls horizontally under the fixed header column. */}
        <div
          ref={viewportRef}
          data-timeline
          className={cn(
            "relative min-w-0 flex-1 overflow-x-auto overflow-y-hidden",
            onSeekBeats && !edit && "cursor-text",
          )}
          // Both axes pan on touch: the timeline is wider than any phone, and
          // `pan-y` alone left everything past the first screen unreachable —
          // the browser refuses to pan sideways and nothing else scrolls it.
          // The ruler and the clips take `pan-y` back for themselves, so
          // scrubbing and dragging a clip still beat the scroll there.
          style={{ height, touchAction: pinching ? "none" : "pan-x pan-y" }}
          onScroll={(e) => {
            const el = e.currentTarget;
            setScrollX(el.scrollLeft);
            // Not one of the view's own scrolls ⇒ the producer's: follow lets go.
            const own = ownScroll.current;
            ownScroll.current = null;
            if (own === null || Math.abs(el.scrollLeft - own) > 1) followPaused.current = true;
            // Run out of timeline and there is more of it: the end of the Set
            // is where you build, so hitting it should not be a wall. Only
            // while zoomed in — at fit there is no edge to reach.
            if (zoom > ZOOM_MIN && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) extendTail();
          }}
          onPointerDown={(e) => {
            if (e.pointerType === "touch") {
              pinch.current.set(e.pointerId, e.clientX);
              if (pinch.current.size === 2) {
                window.clearTimeout(laneHold.current);
                pinchStart.current = { gap: pinchGap(), zoom };
                setPinching(true);
                return;
              }
            }
            beginTimelineGesture(e);
          }}
          onPointerMove={(e) => {
            if (!pinch.current.has(e.pointerId)) return;
            pinch.current.set(e.pointerId, e.clientX);
            const start = pinchStart.current;
            if (!start || start.gap <= 0 || pinch.current.size !== 2) return;
            zoomTo(start.zoom * (pinchGap() / start.gap), pinchMid());
          }}
          onPointerUp={(e) => endPinch(e.pointerId)}
          onPointerCancel={(e) => endPinch(e.pointerId)}
          onDragOver={
            droppable
              ? (e) => {
                  const types = e.dataTransfer.types;
                  const library = edit?.onDropLibrary && types.includes(LIBRARY_DRAG_TYPE);
                  const files = (edit?.onDropFiles || edit?.onDropMidiFiles) && types.includes("Files");
                  const slot = edit?.onDropSlotClip && types.includes(SLOT_DRAG_TYPE);
                  const material = edit?.onDropMaterial && types.includes(MATERIAL_DRAG_TYPE);
                  const mine = edit?.onDropMine && types.includes(MINE_DRAG_TYPE);
                  if (!library && !files && !slot && !material && !mine) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = slot ? "move" : "copy";
                  const lane = laneAt(e.clientY);
                  if (lane !== dropLane) setDropLane(lane);
                }
              : undefined
          }
          onDragLeave={droppable ? () => setDropLane(null) : undefined}
          onDrop={
            droppable && edit
              ? (e) => {
                  setDropLane(null);
                  const lane = lanes[laneAt(e.clientY)];
                  if (!lane) return;
                  const at = snap(beatAt(e.clientX));
                  const payload = e.dataTransfer.getData(LIBRARY_DRAG_TYPE);
                  if (payload && edit.onDropLibrary) {
                    e.preventDefault();
                    edit.onDropLibrary(lane.id, at, payload);
                    return;
                  }
                  const slotClip = e.dataTransfer.getData(SLOT_DRAG_TYPE);
                  if (slotClip && edit.onDropSlotClip) {
                    e.preventDefault();
                    edit.onDropSlotClip(lane.id, at, slotClip);
                    return;
                  }
                  const material = e.dataTransfer.getData(MATERIAL_DRAG_TYPE);
                  if (material && edit.onDropMaterial) {
                    e.preventDefault();
                    edit.onDropMaterial(lane.id, at, material);
                    return;
                  }
                  const mine = e.dataTransfer.getData(MINE_DRAG_TYPE);
                  if (mine && edit.onDropMine) {
                    e.preventDefault();
                    edit.onDropMine(lane.id, at, mine);
                    return;
                  }
                  const midiFiles = [...e.dataTransfer.files].filter(
                    (f) => f.type === "audio/midi" || f.type === "audio/mid" || /\.midi?$/i.test(f.name),
                  );
                  if (midiFiles.length > 0 && edit.onDropMidiFiles) {
                    e.preventDefault();
                    edit.onDropMidiFiles(lane.id, at, midiFiles);
                    return;
                  }
                  const files = [...e.dataTransfer.files].filter(
                    (f) => (f.type.startsWith("audio/") && !midiFiles.includes(f)) || /\.(wav|aiff?|mp3|flac|ogg|m4a)$/i.test(f.name),
                  );
                  if (files.length > 0 && edit.onDropFiles) {
                    e.preventDefault();
                    edit.onDropFiles(lane.id, at, files);
                  }
                }
              : undefined
          }
        >
          <div className="relative" style={{ width: timelineW, height }}>
            <div
              className={cn("relative border-b border-border", onScrubBeats ? "cursor-pointer" : onSeekBeats && "cursor-text")}
              title={onScrubBeats ? "Click or drag to play from here (jumps on the next bar while playing)" : undefined}
              // The ruler is the one place a sideways drag means scrub, not
              // scroll — on touch too, which is why it narrows the viewport's
              // `pan-x pan-y` back down to `pan-y`.
              style={{ height: rulerH, touchAction: "pan-y" }}
              onPointerDown={beginScrub}
              onContextMenu={contextMenu ? (e) => openMenu(e, { kind: "ruler", beat: snap(beatAt(e.clientX)) }) : undefined}
            >
              {loopBeats && loopBeats.end > loopBeats.start && (
                <div
                  className="pointer-events-none absolute bottom-0 h-1.5 rounded-t-sm bg-[var(--changed)]/70"
                  style={{ left: px(loopBeats.start), width: px(loopBeats.end - loopBeats.start) }}
                  title={`Loop ${beats(loopBeats.start)}–${beats(loopBeats.end)}`}
                />
              )}
              {gridBeats.map((b) => (
                <span
                  key={b}
                  className="absolute top-0.5 pl-1 text-[10px] text-muted-foreground tabular"
                  style={{ left: px(b) }}
                >
                  {label(b)}
                </span>
              ))}
              {markers?.map((m) => (
                <button
                  key={`${m.beat}:${m.name}`}
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => onMarkerClick?.(m.beat)}
                  onDoubleClick={() => onMarkerRemove?.(m.beat)}
                  title={`${m.name} — beat ${beats(m.beat)}. Click to jump, double-click to remove.`}
                  className="absolute top-0 z-10 flex h-full max-w-24 items-center gap-0.5 truncate rounded-r-sm border-l-2 border-[var(--changed)] bg-[color-mix(in_srgb,var(--changed)_20%,transparent)] px-1 text-[9px] text-[var(--changed)]"
                  style={{ left: px(m.beat) }}
                >
                  {m.name}
                </button>
              ))}
            </div>

            {fineBeats.map((b) => (
              <div
                key={`fine-${b}`}
                className="pointer-events-none absolute w-px bg-border/30"
                style={{ left: px(b), top: rulerH, bottom: 0 }}
              />
            ))}
            {gridBeats.map((b) => (
              <div
                key={`grid-${b}`}
                className="pointer-events-none absolute w-px bg-border/60"
                style={{ left: px(b), top: rulerH, bottom: 0 }}
              />
            ))}
            {barBeats.map((b) => (
              <div
                key={`bar-${b}`}
                className="pointer-events-none absolute w-px bg-border"
                style={{ left: px(b), top: rulerH, bottom: 0 }}
              />
            ))}

            {insertBeats !== undefined && insertBeats >= 0 && insertBeats <= span && (
              <div
                className="pointer-events-none absolute z-10 w-px animate-pulse"
                style={{ left: px(insertBeats), top: 0, bottom: 0, backgroundColor: "var(--insert, #4aa8ff)" }}
                aria-hidden
              >
                <div
                  className="absolute -left-[4px] top-0 size-0 border-x-[4.5px] border-t-[7px] border-x-transparent"
                  style={{ borderTopColor: "var(--insert, #4aa8ff)" }}
                />
              </div>
            )}
            {playheadBeats !== undefined && playheadBeats >= 0 && playheadBeats <= span && (
              <div
                className="pointer-events-none absolute left-0 z-10 w-px will-change-transform"
                // A transform, not `left`: it moves every frame, and this way
                // the move is the compositor's, with no layout.
                style={{ transform: `translateX(${px(playheadBeats)}px)`, top: 0, bottom: 0, backgroundColor: "var(--primary)" }}
              >
                <div
                  className="absolute -left-[3px] top-0 size-0 border-x-[3.5px] border-t-[6px] border-x-transparent"
                  style={{ borderTopColor: "var(--primary)" }}
                />
              </div>
            )}

            {edit?.timeSelection && !marquee && (() => {
              const sel = edit.timeSelection;
              const rows = lanes.map((l, i) => (sel.laneIds.includes(l.id) ? i : -1)).filter((i) => i >= 0);
              if (rows.length === 0) return null;
              const top = Math.min(...rows);
              const bottom = Math.max(...rows);
              return (
                // Over the clips, as Live tints the selected part of each one.
                <div
                  className="pointer-events-none absolute z-[15] border-x border-[var(--changed)]/80 bg-[color-mix(in_srgb,var(--changed)_22%,transparent)]"
                  style={{
                    left: px(sel.start),
                    width: px(sel.end - sel.start),
                    top: rulerH + top * rowH,
                    height: (bottom - top + 1) * rowH - LANE_GAP,
                  }}
                />
              );
            })()}
            {marquee && (
              <div
                className="pointer-events-none absolute z-20 border border-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_15%,transparent)]"
                style={{
                  left: px(Math.min(marquee.fromBeat, marquee.toBeat)),
                  width: px(Math.abs(marquee.toBeat - marquee.fromBeat)),
                  top: rulerH + Math.min(marquee.fromLane, marquee.toLane) * rowH,
                  height: (Math.abs(marquee.toLane - marquee.fromLane) + 1) * rowH - LANE_GAP,
                }}
              />
            )}

            {overNewLane && (
              // Live's drop area: the track this clip would make, with a plus.
              <div
                className="pointer-events-none absolute inset-x-0 border-y border-dashed border-primary bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]"
                style={{ top: rulerH + lanes.length * rowH, height: laneH }}
              >
                <span className="sticky left-2 inline-flex h-full items-center gap-1.5 text-xs text-foreground">
                  <Plus className="size-4" />
                  New track
                </span>
              </div>
            )}
            {lanes.map((lane, i) => (
              <div
                key={lane.id}
                {...(droppable ? { [DROP_LANE_ATTR]: lane.id } : {})}
                className={cn(
                  "absolute inset-x-0",
                  dropLane === i && "bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]",
                  edit && lane.kind === "midi" && "cursor-cell",
                )}
                data-lane-row
                style={{ top: rulerH + i * rowH, height: laneH, WebkitTouchCallout: contextMenu ? "none" : undefined }}
                onContextMenu={contextMenu ? (e) => openMenu(e, { kind: "lane", lane, beat: snap(beatAt(e.clientX)) }) : undefined}
                onDoubleClick={
                  edit && lane.kind === "midi"
                    ? (e) => {
                        if ((e.target as HTMLElement).closest("button")) return;
                        edit.onCreateMidiClip(lane.id, snap(beatAt(e.clientX)));
                      }
                    : undefined
                }
              >
                {lane.clips.map((clip) => {
                  const fill = clipColor(clip.color, i);
                  const hasNotes = clip.kind === "midi" && (clip.notes?.length ?? 0) > 0;
                  const view = displayed(clip, i);
                  const selected = edit?.selected.has(clip.id) ?? false;
                  const pending = edit?.pending.has(clip.id) ?? false;
                  const dragging = drag?.clipIds.includes(clip.id) ?? false;
                  const widthPx = Math.max(3, px(view.clip.duration));
                  return (
                    <button
                      key={clip.id}
                      type="button"
                      // A clip is a button, which is how a page-level Space key
                      // tells "the transport's own controls, leave them alone"
                      // from "the thing I just tapped".
                      data-clip-id={clip.id}
                      aria-label={clip.name || undefined}
                      // Too narrow for its name to fit in the title bar.
                      title={widthPx < 80 && clip.name ? clip.name : undefined}
                      // A long press on a button can raise the platform's own
                      // callout over the selection it just made; `select-none`
                      // below only covers text selection.
                      onContextMenu={(e) => {
                        if (contextMenu) openMenu(e, { kind: "clip", clip, lane, beat: snap(beatAt(e.clientX)) });
                        else e.preventDefault();
                      }}
                      onMouseEnter={() => setHovered(clip)}
                      onMouseLeave={() => setHovered((c) => (c === clip ? null : c))}
                      onPointerDown={edit ? (e) => beginDrag(e, clip, i) : undefined}
                      onDoubleClick={
                        clip.kind === "midi" && onMidiOpen
                          ? (e) => {
                              e.stopPropagation();
                              onMidiOpen(clip);
                            }
                          : clip.kind !== "midi" && onAudioOpen
                            ? (e) => {
                                e.stopPropagation();
                                onAudioOpen(clip, lane);
                              }
                            : undefined
                      }
                      onClick={(e) => {
                        if (suppressClick.current) {
                          suppressClick.current = false;
                          return;
                        }
                        if (bodyPress.current) {
                          bodyPress.current = false;
                          return;
                        }
                        const additive = e.shiftKey || e.metaKey || e.ctrlKey;
                        edit?.onSelect(clip.id, additive);
                        if (edit && !additive) edit.onTimeSelect?.(null);
                        // A tap inside a clip puts the insert marker where it
                        // landed, so Space plays from there and ⌘E splits
                        // there. The title bar is the clip itself: the
                        // marker goes to its start (the page's onSelect).
                        const onBar = (e.target as HTMLElement).closest("[data-clip-header]") !== null;
                        if (edit && !additive && !onBar) onSeekBeats?.(snap(beatAt(e.clientX)));
                        onClipPlay?.(clip, lane);
                      }}
                      className={cn(
                        "absolute top-1.5 bottom-1.5 select-none overflow-hidden rounded-md border flex flex-col",
                        // A muted track's clips read as muted too: the lane's
                        // own flag, or playback's, was a 9px letter on a phone.
                        (clip.muted || laneMuted(lane, audio)) && "opacity-40",
                        audio?.overridden?.has(lane.id) && "opacity-40 grayscale",
                        // The hatch says "MIDI" on a clip with nothing drawn in
                        // it; over notes it was a second texture to read through.
                        clip.kind === "midi" && !hasNotes &&
                          "[background-image:repeating-linear-gradient(135deg,rgba(0,0,0,.16)_0_3px,transparent_3px_7px)]",
                        edit ? "cursor-grab" : onClipPlay ? "cursor-pointer" : "cursor-default",
                        dragging && "z-20 cursor-grabbing opacity-80",
                        dragging && overNewLane && "opacity-50",
                        selected && "ring-2 ring-white",
                        pending && "border-dashed",
                      )}
                      style={{
                        left: px(view.clip.start),
                        top: (view.laneIndex - i) * rowH + 6,
                        height: rowH - 12,
                        width: widthPx,
                        backgroundColor: `${fill}2e`,
                        borderColor: `${fill}88`,
                        // A finger that starts on a clip is moving the clip,
                        // not panning past it — the viewport's `pan-x` stops
                        // at the clip's edge. Only while editing: with nothing
                        // to drag, a clip should scroll like the rest.
                        touchAction: edit ? "pan-y" : undefined,
                      }}
                    >
                      {/* Colored header bar with clip name */}
                      <div
                        data-clip-header
                        className="flex h-5 shrink-0 items-center px-2 text-[11px] font-semibold"
                        style={{ backgroundColor: fill, color: "#101014" }}
                      >
                        {/* A flex box does not ellipsize its text: the span does. */}
                        {widthPx > 24 && (
                          <span className="min-w-0 truncate">
                            {clip.kind === "midi" ? "♪ " : ""}
                            {clip.name}
                          </span>
                        )}
                        {clip.render === "pending" && widthPx > 40 && <RenderStatus status="pending" className="ml-auto pl-1 opacity-70" />}
                      </div>
                      {/* Waveform / note body */}
                      <div
                        data-clip-body
                        className={cn("relative flex-1 min-h-0", clip.kind === "midi" && "px-1 pt-0.5", edit && "cursor-text")}
                      >
                        {waveform && clip.kind !== "midi" && (
                          <Waveform
                            clip={clip}
                            laneId={lane.id}
                            widthPx={Math.max(1, widthPx - 2)}
                            heightPx={Math.max(1, rowH - 34)}
                            fill={fill}
                            source={waveform}
                          />
                        )}
                        {clip.kind === "midi" && clip.notes && clip.notes.length > 0 && (
                          <NoteSketch notes={clip.notes} duration={clip.duration} heightPx={Math.max(1, rowH - 34)} fill={fill} />
                        )}
                      </div>
                      {edit && widthPx > EDGE_PX * 3 && (
                        <>
                          <span className="absolute inset-y-0 left-0 w-1.5 cursor-col-resize" />
                          <span className="absolute inset-y-0 right-0 w-1.5 cursor-col-resize hover:bg-white/30" />
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <InfoBar
        lanes={lanes}
        length={length}
        zoom={zoom}
        hovered={hovered}
        edit={edit}
        live={live}
        hasClipPlay={!!onClipPlay}
        onClipOpen={onClipOpen}
        onMidiOpen={onMidiOpen}
        hasLoopBeats={!!onLoopBeats}
        narrow={narrow}
        meter={meter}
        grid={
          edit
            ? { label: gridLabel(resolvedGrid, bpb), on: edit.snap !== false && resolvedGrid > 0, onToggle: edit.onSnapToggle }
            : undefined
        }
      />
      {menu && contextMenu && (
        <ContextMenu
          key={menu.key}
          x={menu.x}
          y={menu.y}
          entries={contextMenu(menu.target)}
          label={menuLabel(menu.target)}
          touch={menu.touch}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

/** The menu's accessible name: what it is the menu of. */
function menuLabel(target: ArrangementMenuTarget): string {
  switch (target.kind) {
    case "clip":
      return `Clip ${target.clip.name || ""}`.trim();
    case "lane":
      return `Track ${stripTokens(target.lane.name)}`;
    case "span":
      return "Time selection";
    case "track":
      return `Track ${stripTokens(target.lane.name)}`;
    case "ruler":
      return "Timeline";
  }
}

/**
 * One lane's header. Observing: name, level, flags. Editing: the name is an
 * input, the flags are the Set's own toggles, and a second row carries the
 * mixer — fader and pan proposed to the room on release, so what you hear
 * here is what Live's mixer holds.
 */
/**
 * A finger (or pen) held still: `onHold` at the point it pressed. A mouse is
 * left alone — it has the right button. Presses that start on a control
 * (a fader, a toggle, the name field) are the control's. `consume` is true
 * once after a hold, so the click its lift fires is not also a tap.
 */
function useFingerHold(onHold: (x: number, y: number) => void) {
  const timer = useRef<number | undefined>(undefined);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const holdRef = useRef(onHold);
  holdRef.current = onHold;
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const cancel = () => {
    window.clearTimeout(timer.current);
    origin.current = null;
  };
  return {
    handlers: {
      onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
        fired.current = false;
        if (e.pointerType === "mouse") return;
        const control = (e.target as HTMLElement).closest("input, select, button, [role=slider]");
        if (control && control !== e.currentTarget) return;
        origin.current = { x: e.clientX, y: e.clientY };
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          const at = origin.current;
          origin.current = null;
          if (!at) return;
          fired.current = true;
          navigator.vibrate?.(12);
          holdRef.current(at.x, at.y);
        }, LONG_PRESS_MS);
      },
      onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
        const o = origin.current;
        if (o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > HOLD_SLOP_PX) cancel();
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
    },
    consume: (): boolean => {
      const was = fired.current;
      fired.current = false;
      return was;
    },
  };
}

/** Silenced in the Set, or in this player's playback. */
function laneMuted(lane: Lane, audio: LaneAudioState | undefined): boolean {
  return lane.mute || (audio?.muted.has(lane.id) ?? false);
}

/** "Audio" or "Notes" (MIDI): what a lane holds, in the words the New track
 * sheet uses and the icons the material shelf does. */
function laneKind(kind: Lane["kind"]): { word: string; title: string; Icon: typeof Music } {
  return kind === "midi"
    ? { word: "Notes", title: "Notes track (MIDI)", Icon: Music }
    : { word: "Audio", title: "Audio track", Icon: AudioLines };
}

/**
 * The lane's type as a quiet caption over its name — above it, not beside
 * it, because the name row is already the M/S/arm/patch row and a header is
 * 240px. Shown only where the lane has the height for a third line: on a
 * desktop editor that is a tall lane (the faders take the second line); a
 * phone hides the faders, so a normal lane will do. Too short for it, the
 * name's tooltip says it instead.
 */
function laneKindShows(height: number, editing: boolean): string {
  if (height >= 64) return "flex";
  if (height >= 40) return editing ? "flex wide:hidden" : "flex";
  return "hidden";
}

function LaneKindCaption({ kind, className }: { kind: Lane["kind"]; className: string }) {
  const { word, title, Icon } = laneKind(kind);
  return (
    <span
      title={title}
      className={cn(
        "items-center gap-1 text-[9px] uppercase leading-none tracking-wider text-muted-foreground",
        className,
      )}
    >
      <Icon aria-hidden className="size-2.5 shrink-0" />
      {word}
    </span>
  );
}

function LaneHeader({
  lane,
  index,
  height,
  live,
  edit,
  audio,
  slim,
  dropTarget,
  onMobileTap,
  onMenu,
  onHoldMenu,
}: {
  lane: Lane;
  index: number;
  height: number;
  live: boolean;
  edit: EditState | undefined;
  audio: LaneAudioState | undefined;
  /** Collapsed on a phone: the colour and the flags, nothing that needs width. */
  slim: boolean;
  dropTarget: boolean;
  onMobileTap?: (laneId: string) => void;
  /** A right-click on the header; `rename` focuses its name field when it has one. */
  onMenu?: (e: React.MouseEvent, rename?: () => void) => void;
  /** A finger held on the header: its menu, at that point. */
  onHoldMenu?: (x: number, y: number, rename?: () => void) => void;
}) {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const rename = edit
    ? () => {
        const input = headerRef.current?.querySelector<HTMLInputElement>("input");
        input?.focus();
        input?.select();
      }
    : undefined;
  const hold = useFingerHold((x, y) => onHoldMenu?.(x, y, slim ? undefined : rename));
  const holdProps = onHoldMenu ? hold.handlers : {};
  // The ⟨sb:…⟩ token is identity in Live, not part of the name: never shown,
  // never typed over (a rename re-attaches it — edits.ts setTrackField).
  const name = stripTokens(lane.name);
  // Collapsed, the column is a stack of colour chips that reads as the same
  // key as the clips beside it. Tapping one opens the lane strip, which is
  // where the name and the faders were going to send you anyway.
  if (slim) {
    const muted = audio?.muted.has(lane.id) ?? lane.mute;
    const soloed = audio?.soloed.has(lane.id) ?? lane.solo;
    const kind = laneKind(lane.kind);
    return (
      <button
        type="button"
        className={cn(
          "flex w-full flex-col items-center justify-center gap-1",
          dropTarget && "bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]",
        )}
        style={{ height, marginBottom: LANE_GAP }}
        title={`${name} — ${kind.title}`}
        aria-label={`Track ${name || index + 1}, ${kind.word}`}
        {...holdProps}
        onClick={() => {
          if (hold.consume()) return;
          onMobileTap?.(lane.id);
        }}
        onContextMenu={onMenu ? (e) => onMenu(e) : undefined}
      >
        <span className="flex items-center gap-0.5">
          <span
            className={cn("size-3 shrink-0 rounded-full", muted && "opacity-30")}
            style={{ backgroundColor: clipColor(lane.clips[0]?.color ?? 0, index) }}
          />
          <kind.Icon aria-hidden className="size-2.5 shrink-0 text-muted-foreground" />
        </span>
        {/* The first letters of the name: a column of dots alone left a
            phone with no way to tell Bass from Vox chop. */}
        <span
          aria-hidden
          className={cn(
            "max-w-full overflow-hidden px-0.5 text-[9px] leading-none whitespace-nowrap text-muted-foreground",
            muted && "opacity-50",
          )}
        >
          {name.slice(0, 4)}
        </span>
        {(muted || soloed) && (
          <span
            className="text-[9px] font-bold leading-none"
            style={{ color: soloed ? "var(--solo)" : "var(--mute)" }}
          >
            {soloed ? "S" : "M"}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className={cn(
        "group/lane flex flex-col justify-center gap-0.5 px-1.5 wide:px-2",
        onMobileTap && "cursor-pointer",
        dropTarget && "bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]",
      )}
      ref={headerRef}
      data-lane-header={lane.id}
      // A hold is the menu's, not iOS's text callout.
      style={{ height, marginBottom: LANE_GAP, WebkitTouchCallout: onHoldMenu ? "none" : undefined }}
      {...holdProps}
      onClick={() => {
        if (hold.consume()) return;
        onMobileTap?.(lane.id);
      }}
      onContextMenu={
        onMenu
          ? (e) => {
              // A right-click in the name field is the field's own (paste, spelling).
              if ((e.target as HTMLElement).closest("input, select")) return;
              onMenu(e, rename);
            }
          : undefined
      }
    >
      <LaneKindCaption kind={lane.kind} className={laneKindShows(height, edit !== undefined)} />
      <div className="flex items-center gap-1">
        {edit ? (
          <LaneNameInput name={name} onCommit={(value) => edit.onTrackField(lane.id, "name", value)} />
        ) : (
          <span className="truncate text-[10px] wide:text-xs" title={`${name} — ${laneKind(lane.kind).title}`}>
            {name || <span className="text-muted-foreground">(unnamed)</span>}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {!edit && (
            <>
              {/* The Set's mixer level + pan for this lane, as synced
                  (unity = 0 dB, C = center). */}
              <span
                className="tabular hidden text-[10px] text-muted-foreground wide:inline"
                title={`Mixer volume — fader at ${lane.volume.toFixed(3)}`}
              >
                {faderDbLabel(lane.volume)}
              </span>
              <span
                className="tabular hidden text-[10px] text-muted-foreground wide:inline"
                title={`Mixer pan — knob at ${lane.pan.toFixed(3)}`}
              >
                {panLabel(lane.pan)}
              </span>
            </>
          )}
          {edit && (
            // The Set's own flags, not the player's — toggling these
            // changes the track in everybody's Live.
            <>
              <LaneToggle
                label="M"
                tone="removed"
                active={lane.mute}
                title={`${lane.mute ? "Unmute" : "Mute"} ${name} in the Set`}
                onClick={() => edit.onTrackField(lane.id, "mute", !lane.mute)}
              />
              <LaneToggle
                label="S"
                tone="changed"
                active={lane.solo}
                title={`${lane.solo ? "Unsolo" : "Solo"} ${name} in the Set`}
                onClick={() => edit.onTrackField(lane.id, "solo", !lane.solo)}
              />
              <span className="hidden wide:contents">
              <LaneToggle
                label="●"
                tone="removed"
                active={lane.arm}
                title={`${lane.arm ? "Disarm" : "Arm"} ${name} in the Set`}
                onClick={() => edit.onTrackField(lane.id, "arm", !lane.arm)}
              />
              {edit.onReorderTrack && (
                <>
                  <button
                    type="button"
                    onClick={() => edit.onReorderTrack?.(lane.id, -1)}
                    title={`Move ${name} up — the Set's display order, for everyone`}
                    aria-label={`Move ${name} up`}
                    className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  >
                    <ChevronUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => edit.onReorderTrack?.(lane.id, 1)}
                    title={`Move ${name} down — the Set's display order, for everyone`}
                    aria-label={`Move ${name} down`}
                    className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  >
                    <ChevronDown className="size-3.5" />
                  </button>
                </>
              )}
              {lane.kind === "midi" && audio && <PatchSelect laneId={lane.id} audio={audio} />}
              {lane.kind === "midi" && edit.onAddMidiClip && (
                <button
                  type="button"
                  onClick={() => edit.onAddMidiClip?.(lane.id)}
                  title={`New MIDI clip on ${name} at the insert marker`}
                  aria-label={`New MIDI clip on ${name}`}
                  className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-white/5 hover:text-foreground"
                >
                  <Plus className="size-3.5" />
                </button>
              )}
              {edit.onDuplicateTrack && (
                <button
                  type="button"
                  onClick={() => edit.onDuplicateTrack?.(lane.id)}
                  title={`Duplicate ${name} with its clips`}
                  aria-label={`Duplicate ${name}`}
                  className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-white/5 hover:text-foreground"
                >
                  <Copy className="size-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => edit.onDeleteTrack(lane.id)}
                title={`Delete ${name} and its ${lane.clips.length + lane.sessionClips.length} clip(s) from the Set`}
                aria-label={`Delete ${name}`}
                // Out of sight until the pointer is on the lane: a bin beside
                // M and S on every track is a slip away from a lost track.
                className="flex size-5 items-center justify-center rounded text-muted-foreground opacity-0 hover:text-[var(--removed)] focus-visible:opacity-100 group-hover/lane:opacity-100"
              >
                <Trash2 className="size-3.5" />
              </button>
              </span>
            </>
          )}
          {audio && !edit ? (
            <>
              {lane.kind === "midi" && <PatchSelect laneId={lane.id} audio={audio} />}
              <BackToArrangement lane={lane} audio={audio} />
              {/* Playback mute/solo, separate from the Set's own flags:
                  this is what you are listening to, not what Live stores. */}
              <LaneToggle
                label="M"
                tone="removed"
                active={audio.muted.has(lane.id)}
                title={`${audio.muted.has(lane.id) ? "Unmute" : "Mute"} ${name} in playback`}
                onClick={() => audio.onMute(lane.id, !audio.muted.has(lane.id))}
              />
              <LaneToggle
                label="S"
                tone="changed"
                active={audio.soloed.has(lane.id)}
                title={`${audio.soloed.has(lane.id) ? "Unsolo" : "Solo"} ${name} in playback`}
                onClick={() => audio.onSolo(lane.id, !audio.soloed.has(lane.id))}
              />
            </>
          ) : edit || audio ? null : (
            <>
              {lane.mute && <Flag label="M" tone="removed" />}
              {lane.solo && <Flag label="S" tone="changed" />}
              {lane.arm && <Flag label="●" tone="removed" />}
            </>
          )}
          {!edit && live && lane.rev !== undefined && (
            <span className="tabular text-[10px] text-muted-foreground">r{lane.rev}</span>
          )}
        </span>
      </div>
      {edit && (
        // `min-w-0 overflow-hidden`: the header column is fixed-width, and a
        // row wider than it would paint over the timeline beside it.
        <div className="hidden wide:flex min-w-0 items-center gap-1.5 overflow-hidden">
          <LaneFader
            value={lane.volume}
            min={0}
            max={1}
            label={faderDbLabel(lane.volume)}
            icon={<Volume2 className="size-3" />}
            title="Mixer volume — released value is proposed to the Set"
            onCommit={(v) => edit.onTrackField(lane.id, "volume", v)}
            className="w-14 wide:w-16"
          />
          <LaneFader
            value={lane.pan}
            min={-1}
            max={1}
            label={panLabel(lane.pan)}
            labelClassName="w-6"
            icon={<MoveHorizontal className="size-3" />}
            title="Mixer pan — released value is proposed to the Set"
            onCommit={(v) => edit.onTrackField(lane.id, "pan", v)}
            className="w-10 wide:w-12"
          />
          {audio && (
            <span className="ml-auto flex items-center gap-0.5">
              <BackToArrangement lane={lane} audio={audio} />
              {!audio.mixesTheSet && (
              <>
              <LaneToggle
                label="m"
                tone="removed"
                active={audio.muted.has(lane.id)}
                title={`${audio.muted.has(lane.id) ? "Unmute" : "Mute"} ${name} in playback only (not the Set)`}
                onClick={() => audio.onMute(lane.id, !audio.muted.has(lane.id))}
              />
              <LaneToggle
                label="s"
                tone="changed"
                active={audio.soloed.has(lane.id)}
                title={`${audio.soloed.has(lane.id) ? "Unsolo" : "Solo"} ${name} in playback only (not the Set)`}
                onClick={() => audio.onSolo(lane.id, !audio.soloed.has(lane.id))}
              />
              </>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A mixer control. Dragged locally, proposed ONCE on release — a fader move
 * is dozens of change events, and each would be an intent staling the next.
 * Double-click resets to the parameter's default (unity / center).
 */
function LaneFader({
  value,
  min,
  max,
  label,
  labelClassName = "w-10",
  icon,
  title,
  onCommit,
  className,
}: {
  value: number;
  min: number;
  max: number;
  label: string;
  labelClassName?: string;
  /** Which control this is: two bare sliders side by side read as one. */
  icon?: ReactNode;
  title: string;
  onCommit: (value: number) => void;
  className?: string;
}) {
  const [draft, setDraft] = useState<number | null>(null);
  const commit = () => {
    if (draft === null) return;
    const v = draft;
    setDraft(null);
    if (Math.abs(v - value) > 1e-4) onCommit(v);
  };
  return (
    <span className="flex items-center gap-1" title={title}>
      {icon && <span aria-hidden className="hidden shrink-0 text-muted-foreground wide:inline-flex">{icon}</span>}
      <input
        type="range"
        min={min}
        max={max}
        step={0.005}
        value={draft ?? value}
        onChange={(e) => setDraft(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        onDoubleClick={() => onCommit(min < 0 ? 0 : 0.85)}
        onKeyDown={(e) => e.stopPropagation()}
        className={cn("h-1 cursor-pointer accent-[var(--primary)]", className)}
        aria-label={title}
      />
      <span className={cn("tabular hidden shrink-0 text-[9px] text-muted-foreground wide:inline", labelClassName)}>{label}</span>
    </span>
  );
}

/**
 * Rename a track in place. Held locally while typing and committed on blur or
 * Enter — proposing on every keystroke would be one intent per character, and
 * each would invalidate the next one's baseRev.
 */
function LaneNameInput({ name, onCommit }: { name: string; onCommit: (value: string) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? name;
  const commit = () => {
    setDraft(null);
    // An empty name would leave a lane called only by its token: keep the old one.
    if (draft !== null && draft.trim() !== "" && draft !== name) onCommit(draft);
  };
  return (
    <input
      value={value}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(null);
          e.currentTarget.blur();
        }
        e.stopPropagation(); // the page's clip shortcuts must not fire while typing
      }}
      title={name}
      aria-label={`Rename ${name}`}
      className="min-w-0 flex-1 truncate rounded-sm bg-transparent px-1 text-[10px] outline-none hover:bg-muted focus:bg-muted wide:text-xs"
    />
  );
}

/** Live's per-track "Back to Arrangement": shown only while the Session
 * View has the lane — its timeline clips are silent until this is pressed
 * (or the transport's button returns every lane). */
function BackToArrangement({ lane, audio }: { lane: Lane; audio: LaneAudioState }) {
  if (!audio.onBackToArrangement || !audio.overridden?.has(lane.id)) return null;
  const back = audio.onBackToArrangement;
  return (
    <LaneToggle
      label="↩"
      tone="changed"
      active
      title={`Back to Arrangement — ${lane.name} is playing the Session View; return it to its timeline clips`}
      onClick={() => back(lane.id)}
    />
  );
}

function LaneToggle({
  label,
  tone,
  active,
  title,
  onClick,
}: {
  label: string;
  tone: "removed" | "changed";
  active: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        // 20px: a 14px letter was a hard target for the one control you
        // reach for mid-playback.
        "flex h-5 min-w-[18px] items-center justify-center rounded px-1 text-[10px] font-bold transition-colors",
        active ? "" : "text-muted-foreground hover:text-foreground",
      )}
      style={
        active
          ? { backgroundColor: `color-mix(in srgb, var(--${tone}) 25%, transparent)`, color: `var(--${tone})` }
          : undefined
      }
    >
      {label}
    </button>
  );
}

function Flag({ label, tone }: { label: string; tone: "removed" | "changed" }) {
  return (
    <span
      className="rounded px-1 text-[9px] font-bold leading-4"
      style={{ backgroundColor: `color-mix(in srgb, var(--${tone}) 25%, transparent)`, color: `var(--${tone})` }}
    >
      {label}
    </span>
  );
}

/**
 * A clip's waveform, double-sided, drawn from the player's summary of its
 * object. It appears once the transport has loaded the object, or — where the
 * page offers `request` — once it has been fetched just to draw. Canvas, and
 * one filled path, because a long clip at high zoom is thousands of buckets.
 * Memoised: the view re-renders every frame the transport runs, and a clip
 * that hasn't changed needn't be asked for its peaks again (`source` is
 * rebuilt as its `version` bumps, which is what lets new audio through).
 */
const Waveform = memo(function Waveform({
  clip,
  laneId,
  widthPx,
  heightPx,
  fill,
  source,
}: {
  clip: LaneClip;
  laneId: string;
  widthPx: number;
  heightPx: number;
  /** The clip's colour — the waveform is drawn in it, over its dark body. */
  fill: string;
  source: WaveformSource;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  // A bucket per device pixel, so a retina screen isn't drawn at half detail.
  const dpr = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
  const buckets = Math.max(8, Math.floor(widthPx * dpr));
  const peaks = source.peaksFor(clip, laneId, buckets);
  const { request } = source;
  useEffect(() => {
    request?.(clip, laneId);
  }, [request, clip, laneId]);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !peaks) return;
    const g = prepareCanvas(canvas, widthPx, heightPx);
    if (g) drawEnvelope(g, peaks, widthPx, heightPx, `${fill}d9`);
  }, [peaks, widthPx, heightPx, fill]);
  if (!peaks) return null;
  return (
    <canvas
      ref={ref}
      className="pointer-events-none absolute inset-0"
      style={{ width: widthPx, height: heightPx }}
      aria-hidden
    />
  );
});

/** A MIDI clip's notes as a tiny piano-roll sketch, the way Live paints them.
 * Memoised for the same per-frame reason as `Waveform`. */
const NoteSketch = memo(function NoteSketch({
  notes,
  duration,
  heightPx,
  fill,
}: {
  notes: NonNullable<LaneClip["notes"]>;
  duration: number;
  heightPx: number;
  /** The clip's colour: notes drawn in it, like Live's, read against the
   * clip's own tint where black ones were dark on dark. */
  fill: string;
}) {
  const { low, high } = useMemo(() => {
    let low = 127;
    let high = 0;
    for (const n of notes) {
      if (n.pitch < low) low = n.pitch;
      if (n.pitch > high) high = n.pitch;
    }
    return { low: Math.min(low, high - 6), high };
  }, [notes]);
  const rows = Math.max(1, high - low + 1);
  const rowH = heightPx / rows;
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      width="100%"
      height={heightPx}
      preserveAspectRatio="none"
      viewBox={`0 0 ${Math.max(duration, 0.001)} ${heightPx}`}
      aria-hidden
    >
      {notes.map((n, i) => (
        <rect
          key={i}
          x={n.startTime}
          y={(high - n.pitch) * rowH}
          width={Math.max(n.duration, 0.02)}
          height={Math.max(rowH - 0.5, 1)}
          fill={fill}
          fillOpacity={n.muted ? 0.3 : 0.9}
        />
      ))}
    </svg>
  );
});

/** A control in the phone strip beside the minimap. A real thumb target. */
function StripButton({
  onClick,
  disabled,
  title,
  label,
  pressed,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  label: string;
  /** A toggle's state; absent for a plain button. */
  pressed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
      aria-pressed={pressed}
      className="flex w-10 shrink-0 items-center justify-center border-l border-border text-muted-foreground disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/**
 * The whole arrangement in a strip, with the visible window drawn over it —
 * the way back when zoomed in. Percent-based on purpose: it never scrolls,
 * and clicking or dragging it scrolls the timeline to that beat.
 */
function Minimap({
  lanes,
  span,
  height: fixedHeight,
  window: win,
  playheadBeats,
  onSeekTo,
  onNudge,
}: {
  lanes: Lane[];
  span: number;
  /** Phone strips set their own; a pointer gets the compact computed one. */
  height?: number;
  window: { from: number; width: number };
  playheadBeats: number | undefined;
  onSeekTo: (beat: number) => void;
  /** Put the LEFT edge of the window at this fraction of the timeline. */
  onNudge: (fraction: number) => void;
}) {
  const computed = Math.max(14, lanes.length * Math.max(2, Math.min(4, Math.floor(20 / Math.max(lanes.length, 1)))) + 4);
  const height = fixedHeight ?? computed;
  const rowH = Math.max(2, Math.floor((height - 4) / Math.max(lanes.length, 1)));
  const fractionAt = (clientX: number, el: HTMLElement) => {
    const box = el.getBoundingClientRect();
    return Math.max(0, Math.min((clientX - box.left) / box.width, 1));
  };
  return (
    <div
      className={cn("relative h-full cursor-pointer bg-background/60", fixedHeight === undefined && "border-b border-border")}
      // Without this the browser claims a sideways drag as a page pan and the
      // pointermove listeners below never fire — the strip would only ever
      // take taps on a touchscreen, which is the opposite of the point.
      style={{ height, touchAction: "none" }}
      title="Overview — click or drag to scroll the timeline"
      onPointerDown={(e) => {
        const el = e.currentTarget;
        const at = fractionAt(e.clientX, el);
        // Press on the window itself and it comes with you, by the delta —
        // pressing anywhere else jumps, centring on where you pressed. The
        // old behaviour did the second thing in both cases, so a drag that
        // began on the window snapped it under your finger first.
        const inside = at >= win.from && at <= win.from + win.width;
        const grabOffset = at - win.from;
        if (!inside) onSeekTo(at * span);
        const move = (ev: PointerEvent) => {
          const f = fractionAt(ev.clientX, el);
          if (inside) onNudge(Math.max(0, Math.min(f - grabOffset, 1 - win.width)));
          else onSeekTo(f * span);
        };
        const up = () => {
          window.removeEventListener("pointermove", move);
          window.removeEventListener("pointerup", up);
          window.removeEventListener("pointercancel", up);
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        window.addEventListener("pointercancel", up);
      }}
    >
      {lanes.map((lane, i) =>
        lane.clips.map((c) => (
          <div
            key={c.id}
            className="pointer-events-none absolute rounded-[1px]"
            style={{
              left: `${(c.start / span) * 100}%`,
              width: `max(1px, ${(c.duration / span) * 100}%)`,
              top: 2 + i * rowH,
              height: Math.max(1, rowH - 1),
              backgroundColor: clipColor(c.color, i),
              opacity: c.muted ? 0.35 : 0.9,
            }}
          />
        )),
      )}
      {playheadBeats !== undefined && (
        <div className="pointer-events-none absolute inset-y-0 w-px" style={{ left: `${(playheadBeats / span) * 100}%`, backgroundColor: "var(--primary)" }} />
      )}
      <div
        className="pointer-events-none absolute inset-y-0 border border-foreground/40 bg-foreground/10"
        style={{ left: `${win.from * 100}%`, width: `${win.width * 100}%` }}
      />
    </div>
  );
}

/** Info bar below the timeline — collapsible on mobile, starts collapsed. */
function InfoBar({
  lanes,
  length,
  zoom,
  hovered,
  edit,
  live,
  hasClipPlay,
  onClipOpen,
  onMidiOpen,
  hasLoopBeats,
  narrow,
  meter,
  grid,
}: {
  lanes: Lane[];
  length: number;
  zoom: number;
  hovered: LaneClip | null;
  edit?: EditState;
  live?: boolean;
  hasClipPlay: boolean;
  onClipOpen?: (audioKey: string) => void;
  onMidiOpen?: (clip: LaneClip) => void;
  hasLoopBeats: boolean;
  narrow: boolean;
  meter: Meter;
  grid?: { label: string; on: boolean; onToggle?: () => void };
}) {
  const [expanded, setExpanded] = useState(false);
  // The how-to line, folded away by default: two lines of it under every
  // timeline was a wall to read past to the numbers that change.
  const [tipsOpen, setTipsOpen] = usePref("arrangement.tips", false, isBoolean);
  // Bars, like the ruler and the material shelf, not raw beats.
  const bpb = beatsPerBar(meter);
  const bars = (b: number): string => {
    const n = +(b / bpb).toFixed(2);
    return `${n} bar${n === 1 ? "" : "s"}`;
  };
  const at = (b: number): string => {
    const [bar, beat, tick] = barsBeats(b, meter).split(".");
    return tick === "1" ? `bar ${bar}.${beat}` : `bar ${bar}.${beat}.${tick}`;
  };
  const clipCount = lanes.reduce((n, l) => n + l.clips.length, 0);
  const summary = `${lanes.length} track${lanes.length === 1 ? "" : "s"} · ${clipCount} clip${clipCount === 1 ? "" : "s"} · ${bars(length)}${zoom > 1 ? ` · ${zoom.toFixed(1)}× zoom` : ""}`;

  const readout = hovered ? (
    <>
      <span className="font-medium text-foreground">{hovered.name || "(unnamed)"}</span>
      <span className="tabular">{at(hovered.start)}</span>
      <span className="tabular">{bars(hovered.duration)}</span>
      {hovered.kind === "midi" &&
        (onMidiOpen ? (
          <button
            type="button"
            onClick={() => onMidiOpen(hovered)}
            title={`Open ${hovered.name || "this clip"} in the piano roll`}
            className="tabular underline-offset-2 hover:text-foreground hover:underline"
          >
            MIDI · {hovered.noteCount ?? 0} notes — piano roll
          </button>
        ) : (
          <span className="tabular">MIDI · {hovered.noteCount ?? 0} notes</span>
        ))}
      {hovered.muted && <Badge variant="removed">muted</Badge>}
      {/* The storage key and the sync revision are plumbing: behind the
          tooltip, not in the line a producer reads. */}
      {hovered.audioKey && onClipOpen ? (
        <button
          type="button"
          onClick={() => onClipOpen(hovered.audioKey!)}
          title={`Open ${hovered.audioKey} in the object player${live && hovered.rev !== undefined ? ` · rev ${hovered.rev}` : ""}`}
          className="underline-offset-2 hover:text-foreground hover:underline"
        >
          audio file
        </button>
      ) : hovered.filePath ? (
        <span className="font-mono" title={hovered.filePath}>
          {short(hovered.filePath.split(/[\\/]/).pop() ?? hovered.filePath, 22, 14)}
        </span>
      ) : null}
    </>
  ) : null;
  const tips = [
    ...(edit
      ? [
          "drag a clip's title bar to move it, an edge to trim",
          "click or drag a lane (or a clip's body) to select time",
          "⇧-click extends",
          "double-click a MIDI lane to draw a clip",
          "drop audio files on a lane",
        ]
      : []),
    ...(hasLoopBeats ? ["shift-drag the ruler to loop a region"] : []),
    ...(!edit && hasClipPlay ? ["click a clip to hear it, or the empty timeline to seek"] : []),
    ...(onMidiOpen && lanes.some((l) => l.clips.some((c) => c.kind === "midi"))
      ? ["double-click a MIDI clip for the piano roll"]
      : []),
  ].join(" · ");

  // Desktop: always visible. The hint stays laid out under a hovered clip's
  // readout, so the bar keeps its height: a bar that shrank on hover moved
  // the lanes under the pointer whenever the page was scrolled to its end.
  if (!narrow) {
    return (
      <div className="relative flex min-h-9 flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        <span className={cn("tabular", hovered && "invisible")}>{summary}</span>
        {tips && (
          <button
            type="button"
            onClick={() => setTipsOpen(!tipsOpen)}
            aria-expanded={tipsOpen}
            title={tipsOpen ? "Hide the mouse and keyboard tips" : tips}
            className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:text-foreground", hovered && "invisible")}
          >
            <CircleHelp className="size-3.5" />
            Tips
          </button>
        )}
        {readout && (
          <div className="absolute inset-x-3 top-2 flex items-center gap-x-4 overflow-hidden whitespace-nowrap">{readout}</div>
        )}
        {grid && (
          <button
            type="button"
            onClick={grid.onToggle}
            disabled={!grid.onToggle}
            title="The grid clicks, drags and drops land on — follows the zoom. ⌘1 / ⌘2 narrower / wider, ⌘4 snap on / off, hold ⌘ while dragging to place freely"
            className={cn("ml-auto tabular rounded px-1.5 py-0.5 hover:text-foreground", !grid.on && "line-through opacity-60")}
          >
            Grid {grid.label}
          </button>
        )}
        {tips && tipsOpen && <span className="basis-full">{tips}</span>}
      </div>
    );
  }

  // Mobile: collapsible, starts collapsed
  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-3 py-2 text-[11px] text-muted-foreground"
      >
        <span className="tabular">{summary}</span>
        <svg
          className={`size-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {expanded && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pb-2 text-[11px] text-muted-foreground">
          {readout ?? tips}
        </div>
      )}
    </div>
  );
}
